import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Settings, Plus, Trash2, Briefcase,
  Target, BarChart2, Maximize2, Minimize2, Users, Clock, Check,
  FileText, Cog, GripVertical, ChevronDown, ChevronRight, CheckSquare,
  Calendar, Hash, Type, List, Paperclip, AlignLeft, AlertCircle,
  Expand, Shrink, CircleDot, Lock, ExternalLink,
  UserPlus, GitBranch, Shield, RotateCcw
} from 'lucide-react';
import { CardData, PostIt, PostItType, Action, ExecutionConfig, ExecutionField, ExecutionFieldType, ExecutionChecklistItem, StageRule, ExecutionBlock, AssignmentRoutingConfig, AssignmentTarget, DistributionMode, ApprovalTarget, ApprovalType, AutoRoutingRule } from '../types';

// Tipos para o sistema de abas
type RightPanelTab = 'metrics' | 'execution';
import { DashboardPanel } from './DashboardPanel';
import { KaizenLogPanel } from './KaizenLogPanel';
import { YamazumiPanel } from './YamazumiPanel';
import { useProjectStore } from '../store/useProjectStore';
import { getRoleShiftMinutes } from '../utils/orgShiftUtils';
import { useSimulationSafe } from '../context/SimulationContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useOrgStructure } from '../hooks/useOrgStructure';
import KaizenInspector from './KaizenInspector';
import { ExecutionBlockBuilder } from './ExecutionBlockBuilder';

// --- COMPONENTE DE INPUT DE TEXTO ROBUSTO (Salva Instantaneamente) ---
const BufferedTextInput = ({ 
  value, 
  onCommit, 
  className, 
  placeholder,
  list
}: { 
  value: string | undefined; 
  onCommit: (val: string) => void; 
  className?: string;
  placeholder?: string;
  list?: string;
}) => {
  const [localValue, setLocalValue] = useState<string>('');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    // Salva instantaneamente
    onCommit(newVal);
  };

  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      value={localValue}
      onChange={handleChange}
      onFocus={(e) => e.target.select()}
      list={list}
    />
  );
};

// --- COMPONENTE DE INPUT NUMÉRICO ROBUSTO (Salva Instantaneamente) ---
const BufferedInput = ({ 
  value, 
  onCommit, 
  className, 
  placeholder,
  step = "any",
  min = 0,
  max,
  commitOnChange = true,
}: { 
  value: number | string | undefined; 
  onCommit: (val: number) => void; 
  className?: string;
  placeholder?: string;
  step?: string | number;
  min?: number;
  max?: number;
  commitOnChange?: boolean;
}) => {
  const [localValue, setLocalValue] = useState<string>('');

  useEffect(() => {
    if (value === undefined || value === null) {
      setLocalValue('');
    } else {
      setLocalValue(value.toString());
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setLocalValue(newVal);

    let num = parseFloat(newVal);
    if (!isNaN(num)) {
      if (min !== undefined && num < min) num = min;
      if (max !== undefined && num > max) num = max;
      if (commitOnChange) onCommit(num);
    }
  };

  const commitLocalValue = () => {
    if (localValue === '') {
      onCommit(0);
      return;
    }

    let num = parseFloat(localValue);
    if (isNaN(num)) return;
    if (min !== undefined && num < min) num = min;
    if (max !== undefined && num > max) num = max;
    setLocalValue(num.toString());
    onCommit(num);
  };

  return (
    <input
      type="number"
      step={step}
      className={className}
      placeholder={placeholder}
      value={localValue}
      onChange={handleChange}
      onBlur={commitLocalValue}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commitLocalValue();
          e.currentTarget.blur();
        }
      }}
      onFocus={(e) => e.target.select()}
    />
  );
};

// Lista de sistemas comuns para autocomplete
const COMMON_SYSTEMS = ['SAP', 'Totvs', 'Salesforce', 'Oracle', 'Excel', 'Trello', 'E-mail', 'Manual', 'Senior', 'Omie'];

interface RightPanelProps {
  rightPanelMode: 'none' | 'properties' | 'dashboard' | 'kaizen' | 'yamazumi' | 'simulation';
  dashboardMode: 'sidebar' | 'fullscreen';
  simulationMode: 'sidebar' | 'fullscreen';
  docked?: boolean;
  onClose: () => void;
  onToggleDashboardMode: () => void;
  onToggleSimulationMode: () => void;
  onTogglePathHighlight: (ids: string[] | null) => void;
  highlightedPath: string[] | null;
  onUpdateCardData: (id: string, data: Partial<CardData>) => void;
  postIts: PostIt[];
  cards: CardData[];
  selectedCardId: string | null;
  selectedPostItId: string | null;
  kaizenFilter: 'all' | PostItType;
  onKaizenFilterChange: (filter: 'all' | PostItType) => void;
  isKaizenCollapsed: boolean;
  onToggleKaizenCollapsed: () => void;
  onSelectPostIt: (id: string) => void;
  onReorderPostIts: (postIts: PostIt[]) => void;
  onClearCardFilter: () => void;
  onUpdatePostIt: (id: string, updates: Partial<PostIt>) => void;
  onOpenActionsKaizen: () => void;
  onCreateAction?: (linkedPostItId?: string) => void;
  onOpenActionDetail?: (actionId: string) => void;
  onGenerateFutureMap?: () => void;
  onClearMapFilters?: () => void;
  dashboardFilterResetToken?: number;
  onSimulateSolution?: (suggestion: { focusResource?: string; suggestedExtraFte?: number; suggestedWaitReductionPercent?: number }) => void;
  actions?: Action[];
  metrics?: any;
  /** Quando presente, a aba Execução fica somente-leitura (dados gerenciados pelo blueprint) */
  linkedBlueprintId?: string | null;
  /** Controle de expansão do painel */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  yamazumiAssignments?: Record<string, string>;
  yamazumiShiftMinutes?: number;
  yamazumiFteByRole?: Record<string, number>;
  yamazumiPendingChanges?: number;
  onYamazumiShiftMinutesChange?: (minutes: number) => void;
  onYamazumiFteChange?: (roleKey: string, nextValue: number) => void;
  onYamazumiSync?: () => void;
  onYamazumiAutoBalance?: () => void;
  onYamazumiSaveBaseline?: () => void;
  yamazumiSavedBaseline?: boolean;
  onYamazumiSyncToExecution?: () => void | Promise<void>;
  syncingYamazumiToExecution?: boolean;
  /** Controlled zoom synced with YamazumiSimulator */
  yamazumiZoom?: number;
  onYamazumiZoomChange?: (z: number) => void;
  /** Demand simulation % synced with YamazumiSimulator */
  yamazumiDemandaPercent?: number;
  onYamazumiDemandaPercentChange?: (v: number) => void;
  /** Snapshot of saved baseline KPIs for Δ BASE display */
  yamazumiSavedBaselineKpis?: { occupation: number; leadMinutes: number } | null;
}

interface ResourceItem {
  role: string;
  quantity: number;
}

// --- COLLAPSIBLE SECTION ---
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, defaultOpen = true, children, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`border-b border-slate-200/60 dark:border-slate-700/60 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 px-4 py-2.5 text-left transition-colors hover:bg-slate-100/50 dark:hover:bg-slate-700/30"
      >
        {open ? <ChevronDown size={11} className="text-slate-400 shrink-0" /> : <ChevronRight size={11} className="text-slate-400 shrink-0" />}
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {icon} {title}
        </span>
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

// ============================================================================
// ASSIGNMENT & ROUTING SECTION
// ============================================================================

const DEFAULT_ROUTING: AssignmentRoutingConfig = {
  assignTo: 'role',
  distributionMode: 'queue',
  requireApproval: false,
};

const ASSIGN_OPTIONS: { value: AssignmentTarget; label: string; desc: string }[] = [
  { value: 'user', label: 'Usuário específico', desc: 'Atribuir diretamente a um usuário' },
  { value: 'role', label: 'Cargo / Função', desc: 'Enviar para quem ocupa a função' },
  { value: 'department', label: 'Departamento', desc: 'Distribuir para o departamento' },
  { value: 'auto', label: 'Automático (regra)', desc: 'Em breve: distribuição baseada em regras' },
];

const APPROVAL_TARGET_OPTIONS: { value: ApprovalTarget; label: string }[] = [
  { value: 'user', label: 'Usuário específico' },
  { value: 'role', label: 'Cargo / Função' },
  { value: 'manager', label: 'Gestor do responsável' },
];

const SLA_ESCALATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'alert', label: 'Notificar responsável' },
  { value: 'notify_manager', label: 'Notificar gestor' },
  { value: 'escalate', label: 'Escalar tarefa' },
  { value: 'reassign', label: 'Reatribuir automaticamente' },
];

const AssignmentRoutingSection: React.FC<{
  routing?: AssignmentRoutingConfig;
  slaActions?: string[];
  hasSLA: boolean;
  readOnly: boolean;
  onUpdate: (routing: AssignmentRoutingConfig) => void;
  onUpdateSlaActions: (actions: string[]) => void;
}> = ({ routing: routingProp, readOnly, onUpdate, hasSLA }) => {
  const routing = routingProp ?? DEFAULT_ROUTING;
  const update = (u: Partial<AssignmentRoutingConfig>) => onUpdate({ ...routing, ...u });

  // Force direct distribution when user is selected
  const effectiveDistribution: DistributionMode =
    routing.assignTo === 'user' ? 'direct' : routing.distributionMode;
  const distributionLocked = routing.assignTo === 'user';

  const radioClass = (selected: boolean) =>
    `flex items-start gap-2 rounded border px-2.5 py-2 text-left transition-all cursor-pointer ${
      selected
        ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-900/15 text-blue-700 dark:text-blue-300'
        : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300'
    }`;

  const inputClass = 'w-full bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[11px] px-2.5 py-1.5 rounded focus:outline-none focus:border-blue-400 text-slate-700 dark:text-slate-200';

  // Summary labels
  const assignLabel = routing.assignTo === 'user' ? (routing.assignTargetLabel || 'Usuário') :
    routing.assignTo === 'role' ? (routing.assignTargetLabel || 'Função') :
    routing.assignTo === 'department' ? (routing.assignTargetLabel || 'Departamento') : 'Automático';
  const distLabel = effectiveDistribution === 'direct' ? 'Atribuição direta' : 'Fila (claim)';

  return (
    <CollapsibleSection
      title="Responsável e Fluxo"
      icon={<UserPlus size={11} />}
      defaultOpen
    >
      <p className="mb-3 text-[9px] text-slate-400 dark:text-slate-500">
        Defina quem executa esta atividade e como ela será distribuída na operação
      </p>

      {/* ── 1. ENVIAR PARA ── */}
      <div className="space-y-2 mb-4">
        <label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          <Users size={10} /> Enviar para
        </label>
        <div className="grid grid-cols-1 gap-1.5">
          {ASSIGN_OPTIONS.map(opt => (
            <div key={opt.value}>
              <button
                type="button"
                disabled={readOnly}
                onClick={() => {
                  const updates: Partial<AssignmentRoutingConfig> = {
                    assignTo: opt.value,
                    assignTargetId: undefined,
                    assignTargetLabel: undefined,
                  };
                  // Force direct when user selected
                  if (opt.value === 'user') updates.distributionMode = 'direct';
                  update(updates);
                }}
                className={radioClass(routing.assignTo === opt.value)}
              >
                <div className={`mt-0.5 w-3 h-3 rounded-full border-2 shrink-0 ${routing.assignTo === opt.value ? 'border-blue-500 bg-blue-500' : 'border-slate-300 dark:border-slate-500'}`}>
                  {routing.assignTo === opt.value && <div className="w-1 h-1 bg-white rounded-full m-auto mt-[2px]" />}
                </div>
                <div className="min-w-0">
                  <span className="text-[11px] font-semibold block">{opt.label}</span>
                  <span className="text-[9px] opacity-60">{opt.desc}</span>
                </div>
              </button>

              {/* Specific input for each target type */}
              {routing.assignTo === opt.value && opt.value === 'user' && (
                <div className="ml-5 mt-1.5">
                  <input
                    type="text"
                    value={routing.assignTargetLabel ?? ''}
                    onChange={(e) => update({ assignTargetLabel: e.target.value, assignTargetId: e.target.value })}
                    readOnly={readOnly}
                    className={inputClass}
                    placeholder="Nome do usuário..."
                  />
                  <p className="mt-0.5 text-[8px] text-slate-400 dark:text-slate-500">Salva em: assigned_user_id</p>
                </div>
              )}
              {routing.assignTo === opt.value && opt.value === 'role' && (
                <div className="ml-5 mt-1.5">
                  <input
                    type="text"
                    value={routing.assignTargetLabel ?? ''}
                    onChange={(e) => update({ assignTargetLabel: e.target.value, assignTargetId: e.target.value })}
                    readOnly={readOnly}
                    className={inputClass}
                    placeholder="Nome da função (ex: Analista, Supervisor)..."
                  />
                  <p className="mt-0.5 text-[8px] text-slate-400 dark:text-slate-500">Salva em: assigned_role_id (workflow_roles_v9)</p>
                </div>
              )}
              {routing.assignTo === opt.value && opt.value === 'department' && (
                <div className="ml-5 mt-1.5">
                  <input
                    type="text"
                    value={routing.assignTargetLabel ?? ''}
                    onChange={(e) => update({ assignTargetLabel: e.target.value, assignTargetId: e.target.value })}
                    readOnly={readOnly}
                    className={inputClass}
                    placeholder="Nome do departamento..."
                  />
                  <p className="mt-0.5 text-[8px] text-slate-400 dark:text-slate-500">Backend decide distribuição dentro do departamento</p>
                </div>
              )}
              {routing.assignTo === opt.value && opt.value === 'auto' && (
                <div className="ml-5 mt-1.5 px-2.5 py-2 bg-slate-50 dark:bg-slate-700/50 rounded border border-dashed border-slate-200 dark:border-slate-600">
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 italic">
                    Em breve: distribuição automática baseada em regras de negócio
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── 2. MODO DE EXECUÇÃO ── */}
      <div className="space-y-2 mb-4">
        <label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          <GitBranch size={10} /> Modo de execução
        </label>
        {distributionLocked && (
          <p className="text-[9px] text-amber-600/80 dark:text-amber-400/70 flex items-center gap-1">
            <Lock size={9} /> Usuário específico requer atribuição direta
          </p>
        )}
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            disabled={readOnly || distributionLocked}
            onClick={() => update({ distributionMode: 'direct' })}
            className={`${radioClass(effectiveDistribution === 'direct')} flex-col items-center text-center ${distributionLocked ? 'opacity-70' : ''}`}
          >
            <span className="text-[11px] font-semibold">Atribuição direta</span>
            <span className="text-[8px] opacity-50 leading-tight">Tarefa já chega com responsável definido</span>
          </button>
          <button
            type="button"
            disabled={readOnly || distributionLocked}
            onClick={() => update({ distributionMode: 'queue' })}
            className={`${radioClass(effectiveDistribution === 'queue')} flex-col items-center text-center ${distributionLocked ? 'opacity-70' : ''}`}
          >
            <span className="text-[11px] font-semibold">Fila (claim)</span>
            <span className="text-[8px] opacity-50 leading-tight">Qualquer pessoa da função pode assumir</span>
          </button>
        </div>
      </div>

      {/* ── 3. APROVAÇÃO ── */}
      <div className="space-y-2 mb-4">
        <label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          <Shield size={10} /> Aprovação necessária?
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            disabled={readOnly}
            onClick={() => update({ requireApproval: false })}
            className={radioClass(!routing.requireApproval) + ' justify-center'}
          >
            <span className="text-[11px] font-semibold">Não</span>
          </button>
          <button
            type="button"
            disabled={readOnly}
            onClick={() => update({ requireApproval: true })}
            className={radioClass(routing.requireApproval) + ' justify-center'}
          >
            <span className="text-[11px] font-semibold">Sim</span>
          </button>
        </div>

        {routing.requireApproval && (
          <div className="space-y-2 ml-2 pl-2 border-l-2 border-blue-100 dark:border-blue-900">
            <p className="text-[9px] text-slate-400 dark:text-slate-500">
              Após concluir, enviar para aprovação
            </p>
            <div>
              <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1 block">Quem aprova?</label>
              <div className="space-y-1">
                {APPROVAL_TARGET_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={readOnly}
                    onClick={() => update({ approvalTarget: opt.value, approvalTargetId: undefined, approvalTargetLabel: undefined })}
                    className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors ${
                      routing.approvalTarget === opt.value
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {routing.approvalTarget && routing.approvalTarget !== 'manager' && (
              <input
                type="text"
                value={routing.approvalTargetLabel ?? ''}
                onChange={(e) => update({ approvalTargetLabel: e.target.value, approvalTargetId: e.target.value })}
                readOnly={readOnly}
                className={inputClass}
                placeholder={routing.approvalTarget === 'role' ? 'Função aprovadora...' : 'Nome do aprovador...'}
              />
            )}

            <div>
              <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1 block">Tipo de aprovação</label>
              <div className="grid grid-cols-2 gap-1.5">
                {(['single', 'multiple'] as ApprovalType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    disabled={readOnly}
                    onClick={() => update({ approvalType: t })}
                    className={`text-center px-2 py-1.5 rounded border text-[10px] font-semibold transition-colors ${
                      (routing.approvalType ?? 'single') === t
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-slate-200 dark:border-slate-600 text-slate-400 hover:border-slate-300'
                    }`}
                  >
                    {t === 'single' ? '1 Aprovador' : 'Múltiplos'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 4. SLA + ATRASO ── */}
      {hasSLA && (
        <div className="space-y-2 mb-4">
          <label className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <RotateCcw size={10} /> Se atrasar
          </label>
          <div className="space-y-1">
            {SLA_ESCALATION_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(routing.slaEscalationActions ?? []).includes(opt.value as any)}
                  onChange={(e) => {
                    const current = routing.slaEscalationActions ?? [];
                    const updated = e.target.checked
                      ? [...current, opt.value as any]
                      : current.filter(a => a !== opt.value);
                    update({ slaEscalationActions: updated });
                  }}
                  disabled={readOnly}
                  className="rounded border-slate-300 dark:border-slate-600 text-blue-600"
                />
                <span className="text-[11px] text-slate-600 dark:text-slate-300">{opt.label}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-slate-500 dark:text-slate-400 shrink-0">Considerar atraso após:</span>
            <input
              type="number"
              value={routing.slaGraceHours ?? ''}
              onChange={(e) => update({ slaGraceHours: e.target.value ? Number(e.target.value) : undefined })}
              readOnly={readOnly}
              className={inputClass + ' !w-16 text-center'}
              placeholder="0"
              min={0}
            />
            <span className="text-[10px] text-slate-400">horas após SLA</span>
          </div>
        </div>
      )}

      {/* ── 5. CONTEXTO OPERACIONAL ── */}
      <p className="text-[9px] text-slate-400 dark:text-slate-500 mb-3 flex items-center gap-1">
        <Cog size={8} className="text-slate-400/60" />
        Essa tarefa aparecerá na mesa correspondente durante a execução
      </p>

      {/* ── 6. RESUMO DA DISTRIBUIÇÃO ── */}
      <div className="bg-slate-50 dark:bg-slate-700/40 rounded border border-slate-200/80 dark:border-slate-600/50 px-3 py-2">
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
          Resumo da distribuição
        </p>
        <div className="space-y-0.5 text-[10px]">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">→</span>
            <span className="text-slate-500 dark:text-slate-400">Enviado para:</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{assignLabel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">→</span>
            <span className="text-slate-500 dark:text-slate-400">Tipo:</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{distLabel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">→</span>
            <span className="text-slate-500 dark:text-slate-400">Aprovação:</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{routing.requireApproval ? 'Sim' : 'Não'}</span>
          </div>
          {hasSLA && (routing.slaEscalationActions ?? []).length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">→</span>
              <span className="text-slate-500 dark:text-slate-400">Atraso:</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200">{(routing.slaEscalationActions ?? []).length} ações</span>
            </div>
          )}
        </div>
      </div>

      {/* Micro copy */}
      <p className="mt-2 text-[9px] text-slate-400 dark:text-slate-500 italic">
        Essas configurações definem como a tarefa será distribuída e executada na operação real
      </p>
    </CollapsibleSection>
  );
};

// --- EXECUTION TAB CONTENT (extracted for clarity) ---
const ExecutionTabContent: React.FC<{
  executionConfig: ExecutionConfig;
  isExecutionReadOnly: boolean;
  isExpanded: boolean;
  onUpdateExecutionConfig: (updates: Partial<ExecutionConfig>) => void;
  sectionClass: string;
  labelClass: string;
  inputClass: string;
}> = ({ executionConfig, isExecutionReadOnly, isExpanded, onUpdateExecutionConfig, sectionClass, labelClass, inputClass }) => {
  const hasInstruction = !!(executionConfig.instruction?.trim());
  const hasSLA = executionConfig.slaHours != null && Number(executionConfig.slaHours) > 0;

  return (
    <div className="space-y-0">
      {/* Context banner */}
      <div className="px-4 py-2 border-b border-slate-200/40 dark:border-slate-700/40 bg-blue-50/30 dark:bg-blue-900/5">
        <p className="text-[9px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
          <Cog size={9} className="text-blue-500/60" />
          Este conteúdo será utilizado na execução real do processo
        </p>
      </div>

      {/* ═══ SOP — BLOCO PRINCIPAL ═══ */}
      <div className="border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center gap-1.5">
            <FileText size={12} className="text-blue-500 dark:text-blue-400" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
              Instrução de Trabalho (SOP)
            </span>
          </div>
          <p className="mt-0.5 text-[9px] text-slate-400 dark:text-slate-500">
            Defina como esta atividade deve ser executada na operação real
          </p>
        </div>
        <div className="px-4 pb-3">
          <textarea
            value={executionConfig.instruction ?? ''}
            onChange={(e) => onUpdateExecutionConfig({ instruction: e.target.value })}
            readOnly={isExecutionReadOnly}
            className={inputClass + ` !h-auto py-2 text-[11px] leading-relaxed resize-y ${isExpanded ? 'min-h-[160px]' : 'min-h-[100px]'}`}
            placeholder="Descreva o passo a passo da execução (instruções, regras, cuidados, padrões...)"
            rows={isExpanded ? 8 : 5}
          />
          {isExecutionReadOnly && (
            <p className="mt-1 flex items-center gap-1 text-[9px] text-amber-600 dark:text-amber-400">
              <Lock size={9} /> Vinculado a blueprint publicado
            </p>
          )}
        </div>
      </div>

      {/* ═══ ATRIBUIÇÃO E ROTEAMENTO ═══ */}
      <AssignmentRoutingSection
        routing={executionConfig.routing}
        slaActions={executionConfig.slaActions}
        hasSLA={hasSLA}
        readOnly={isExecutionReadOnly}
        onUpdate={(routing) => onUpdateExecutionConfig({ routing })}
        onUpdateSlaActions={(slaActions) => onUpdateExecutionConfig({ slaActions })}
      />

      {/* Tipo de Conclusão */}
      <CollapsibleSection title="Tipo de Conclusão" icon={<Cog size={11} />} defaultOpen>
        <p className="mb-2 text-[9px] text-slate-400 dark:text-slate-500">Define como a tarefa será finalizada na operação</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => !isExecutionReadOnly && onUpdateExecutionConfig({ actionType: 'simple_complete' })}
            className={`flex flex-col items-center gap-1 rounded border px-2 py-2.5 text-center transition-all ${
              executionConfig.actionType === 'simple_complete'
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300'
            }`}
          >
            <Check size={16} />
            <span className="text-[10px] font-bold">Concluir</span>
          </button>
          <button
            type="button"
            onClick={() => !isExecutionReadOnly && onUpdateExecutionConfig({ actionType: 'approve_reject' })}
            className={`flex flex-col items-center gap-1 rounded border px-2 py-2.5 text-center transition-all ${
              executionConfig.actionType === 'approve_reject'
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300'
            }`}
          >
            <CircleDot size={16} />
            <span className="text-[10px] font-bold">Aprovar / Rejeitar</span>
          </button>
        </div>
      </CollapsibleSection>

      {/* SLA */}
      <CollapsibleSection title="SLA" icon={<Clock size={11} />} defaultOpen>
        <div>
          <BufferedInput
            value={executionConfig.slaHours ?? ''}
            onCommit={(val) => onUpdateExecutionConfig({ slaHours: val || undefined })}
            className={inputClass}
            placeholder="Ex: 24 (horas)"
            min={0}
            step={1}
          />
          <p className="mt-1 text-[9px] text-slate-400 dark:text-slate-500">Tempo máximo para execução antes de gerar alerta</p>
        </div>

        {/* SLA Actions */}
        {hasSLA && (
          <div className="mt-3 space-y-2">
            <label className={labelClass}>Ações ao Estourar SLA</label>
            {(['alert', 'escalate', 'block_completion'] as const).map((action) => (
              <label key={action} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(executionConfig.slaActions ?? []).includes(action)} onChange={(e) => {
                  const current = executionConfig.slaActions ?? [];
                  const updated = e.target.checked ? [...current, action] : current.filter(a => a !== action);
                  onUpdateExecutionConfig({ slaActions: updated });
                }} disabled={isExecutionReadOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
                <span className="text-[11px] text-slate-600 dark:text-slate-300">
                  {action === 'alert' ? 'Alertar responsável' : action === 'escalate' ? 'Escalar para gestor' : 'Bloquear conclusão'}
                </span>
              </label>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Blocos de Execução */}
      <CollapsibleSection title="Blocos de Execução" icon={<List size={11} />} defaultOpen={isExpanded}>
        <ExecutionBlockBuilder
          blocks={executionConfig.blocks ?? []}
          onChange={(blocks) => onUpdateExecutionConfig({ blocks })}
          readOnly={isExecutionReadOnly}
        />
      </CollapsibleSection>

      {/* ═══ FOOTER CTA ═══ */}
      <div className="px-4 py-3 border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2 text-[9px] text-slate-400 dark:text-slate-500">
          {hasInstruction && <span className="flex items-center gap-0.5"><FileText size={9} className="text-blue-500/60" /> SOP</span>}
          {executionConfig.routing?.assignTo && <span className="flex items-center gap-0.5"><Users size={9} className="text-slate-500/60" /> {executionConfig.routing.assignTo === 'user' ? 'Direto' : executionConfig.routing.assignTo === 'role' ? 'Função' : executionConfig.routing.assignTo === 'department' ? 'Depto' : 'Auto'}</span>}
          {hasSLA && <span className="flex items-center gap-0.5"><Clock size={9} className="text-amber-500/60" /> {executionConfig.slaHours}h</span>}
          {(executionConfig.blocks ?? []).length > 0 && <span className="flex items-center gap-0.5"><List size={9} className="text-slate-500/60" /> {(executionConfig.blocks ?? []).length} blocos</span>}
          {!hasInstruction && !hasSLA && !(executionConfig.blocks ?? []).length && <span className="italic">Configuração pendente</span>}
        </div>
      </div>
    </div>
  );
};

// ─── Creatable Department Field ──────────────────────────────────────────────
interface CreatableDeptProps {
  value: string;
  officialDepts: { id: string; name: string; visible?: boolean }[];
  suggestions: string[];
  onChange: (val: string) => void;
  className?: string;
}

const CreatableDepartmentField: React.FC<CreatableDeptProps> = ({ value, officialDepts, suggestions, onChange, className }) => {
  const [inputVal, setInputVal] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => { setInputVal(value || ''); }, [value]);

  const visibleOfficial = officialDepts.filter(d => d.visible !== false);
  const filtered = inputVal.trim()
    ? [
        ...visibleOfficial.filter(d => d.name.toLowerCase().includes(inputVal.toLowerCase())),
        ...suggestions.filter(s => s.toLowerCase().includes(inputVal.toLowerCase())),
      ]
    : [...visibleOfficial, ...suggestions];

  const isOfficial = (name: string) => visibleOfficial.some(d => d.name === name);
  const isSuggestion = (name: string) => suggestions.includes(name);

  const select = (name: string) => { onChange(name); setInputVal(name); setOpen(false); };
  const createNew = () => { if (inputVal.trim()) { onChange(inputVal.trim()); setOpen(false); } };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentIsOfficial = isOfficial(value);
  const currentIsSuggestion = isSuggestion(value);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative flex items-center">
        <input
          type="text"
          value={inputVal}
          onChange={e => { setInputVal(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const match = filtered[0]; match ? select(typeof match === 'string' ? match : match.name) : createNew(); } if (e.key === 'Escape') setOpen(false); }}
          placeholder="Departamento..."
          className={`${className || ''} pr-6`}
          style={currentIsSuggestion ? { borderStyle: 'dashed' } : undefined}
        />
        {currentIsOfficial && (
          <Check size={10} className="absolute right-2 text-emerald-500 pointer-events-none" />
        )}
        {currentIsSuggestion && (
          <span className="absolute right-2 text-[9px] text-amber-500 font-bold pointer-events-none leading-none">tmp</span>
        )}
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded shadow-md max-h-40 overflow-y-auto text-xs">
          {visibleOfficial.length > 0 && (
            <div className="px-2 py-1 text-[10px] text-gray-400 uppercase tracking-wide font-semibold border-b">Admin</div>
          )}
          {visibleOfficial.filter(d => !inputVal.trim() || d.name.toLowerCase().includes(inputVal.toLowerCase())).map(d => (
            <button key={d.id} type="button" onMouseDown={() => select(d.name)}
              className={`w-full text-left px-2 py-1.5 flex items-center gap-1.5 hover:bg-gray-50 ${value === d.name ? 'bg-blue-50 text-blue-700' : 'text-gray-800'}`}>
              <Check size={10} className="text-emerald-500 shrink-0" />
              {d.name}
            </button>
          ))}
          {suggestions.filter(s => !inputVal.trim() || s.toLowerCase().includes(inputVal.toLowerCase())).length > 0 && (
            <div className="px-2 py-1 text-[10px] text-amber-500 uppercase tracking-wide font-semibold border-b border-t mt-0.5">Temporários</div>
          )}
          {suggestions.filter(s => !inputVal.trim() || s.toLowerCase().includes(inputVal.toLowerCase())).map(s => (
            <button key={s} type="button" onMouseDown={() => select(s)}
              className={`w-full text-left px-2 py-1.5 flex items-center gap-1.5 hover:bg-amber-50 border-l-2 border-dashed border-amber-300 ${value === s ? 'bg-amber-50 text-amber-700' : 'text-gray-700'}`}>
              <span className="text-[9px] font-bold text-amber-500 shrink-0">tmp</span>
              {s}
            </button>
          ))}
          {inputVal.trim() && !visibleOfficial.some(d => d.name.toLowerCase() === inputVal.toLowerCase()) && !suggestions.some(s => s.toLowerCase() === inputVal.toLowerCase()) && (
            <>
              <div className="border-t" />
              <button type="button" onMouseDown={createNew}
                className="w-full text-left px-2 py-1.5 flex items-center gap-1.5 hover:bg-blue-50 text-blue-600 font-medium">
                <Plus size={10} className="shrink-0" />
                Criar "{inputVal.trim()}"
              </button>
            </>
          )}
          {filtered.length === 0 && !inputVal.trim() && (
            <div className="px-2 py-2 text-gray-400 text-center">Nenhum departamento cadastrado</div>
          )}
        </div>
      )}
    </div>
  );
};

// Referência estável para array vazio — evita que o selector ?? [] crie novo array a cada render
const EMPTY_CARDS: CardData[] = [];

export const RightPanel: React.FC<RightPanelProps> = ({
  rightPanelMode,
  dashboardMode,
  simulationMode,
  docked = false,
  onClose,
  onToggleDashboardMode,
  onToggleSimulationMode,
  onTogglePathHighlight,
  highlightedPath,
  onUpdateCardData,
  cards,
  selectedCardId,
  selectedPostItId,
  kaizenFilter,
  onKaizenFilterChange,
  isKaizenCollapsed,
  onToggleKaizenCollapsed,
  onSelectPostIt,
  onReorderPostIts,
  onClearCardFilter,
  onUpdatePostIt,
  onOpenActionsKaizen,
  onCreateAction,
  onOpenActionDetail,
  onGenerateFutureMap,
  onClearMapFilters,
  dashboardFilterResetToken = 0,
  onSimulateSolution,
  actions = [],
  metrics,
  linkedBlueprintId,
  isExpanded = false,
  onExpandedChange,
  yamazumiAssignments,
  yamazumiShiftMinutes,
  yamazumiFteByRole,
  yamazumiPendingChanges,
  onYamazumiShiftMinutesChange,
  onYamazumiFteChange,
  onYamazumiSync,
  onYamazumiAutoBalance,
  onYamazumiSaveBaseline,
  yamazumiSavedBaseline,
  onYamazumiSyncToExecution,
  syncingYamazumiToExecution,
  yamazumiZoom,
  onYamazumiZoomChange,
  yamazumiDemandaPercent,
  onYamazumiDemandaPercentChange,
  yamazumiSavedBaselineKpis,
}) => {
  // ============================================================================
  // ⚠️ IMPORTANTE: TODOS OS HOOKS DEVEM VIR ANTES DE QUALQUER RETURN CONDICIONAL
  // ============================================================================
  
  // Buscar departamentos da organização (DB-backed SSOT)
  const organizationId = useOrganizationId();
  const { departments: orgDepartments, getRolesForDepartment, isLoading: orgStructureLoading } = useOrgStructure(organizationId);
  // Fallback para dados in-memory enquanto migração não roda
  const organizationFallback = useProjectStore(state => state.organization);
  const sectorSuggestions = useProjectStore(state => state.sectorSuggestions);
  const addSectorSuggestion = useProjectStore(state => state.addSectorSuggestion);
  const mapType = useProjectStore(state => state.mapType);
  const viewMode = useProjectStore(state => state.viewMode);
  const postIts = useProjectStore(state => state.postIts);
  const setCards = useProjectStore(state => state.setCards);
  // V104.0 — Configuração Global de Fábrica
  const factoryShifts     = useProjectStore(state => state.factoryShifts);
  const factoryMinPerShift = useProjectStore(state => state.factoryMinPerShift);
  const factoryWorkDays   = useProjectStore(state => state.factoryWorkDays);
  const setFactoryShifts     = useProjectStore(state => state.setFactoryShifts);
  const setFactoryMinPerShift = useProjectStore(state => state.setFactoryMinPerShift);
  const setFactoryWorkDays   = useProjectStore(state => state.setFactoryWorkDays);
  // Todos os cards da sheet ativa — para cálculos cross-card (ex: Takt Time color)
  // NOTA: usar constante EMPTY_CARDS fora do componente para evitar loop no useSyncExternalStore
  const allSheetCards = useProjectStore(state => {
    const sheet = state.sheets.find(s => s.id === state.activeSheetId);
    return sheet?.cards ?? EMPTY_CARDS;
  });

  // Subscricao direta ao card selecionado na store → todos os campos do painel atualizam imediatamente
  const selectedCard = useProjectStore(state => {
    if (!selectedCardId) return null;
    const sheet = state.sheets.find(s => s.id === state.activeSheetId);
    return sheet?.cards.find(c => c.id === selectedCardId) ?? null;
  });

  const hasLinkedReductionKaizen = selectedCard ? postIts.some((postIt) => {
    if (postIt.type !== 'action') return false;
    const hasReductionImpact = postIt.impactos?.some((impact) => impact.targetNodeId === selectedCard.id && impact.reducao > 0);
    const textMentionsReduction = /redu[cç][aã]o|reducao/i.test(postIt.text ?? '');
    return Boolean(hasReductionImpact || (postIt.fixedToNodeId === selectedCard.id && textMentionsReduction));
  }) : false;
  const lockRootVsmParameters = viewMode === 'kaizen_work' && !hasLinkedReductionKaizen;
  
  // Estado da aba ativa (Métricas vs Execução)
  const [activeTab, setActiveTab] = useState<RightPanelTab>('metrics');
  const hasOpenedOnce = useRef(false);

  // Primeira vez que um card é selecionado → abre em Métricas.
  // Nas vezes seguintes → mantém a aba que estava ativa.
  useEffect(() => {
    if (selectedCardId && !hasOpenedOnce.current) {
      hasOpenedOnce.current = true;
      setActiveTab('metrics');
    }
  }, [selectedCardId]);
  
  // Estado para Form Builder
  const [showFieldTypeMenu, setShowFieldTypeMenu] = useState(false);
  const simulation = useSimulationSafe();
  const isSimulationMode = Boolean(simulation?.isActive);
  
  // ============================================================================
  // FIM DOS HOOKS - AGORA PODEMOS TER RETURNS CONDICIONAIS
  // ============================================================================
  
  const SHIFT_MINUTES = (() => {
    const roleId = selectedCard?.resources?.[0]?.roleId;
    if (roleId) return getRoleShiftMinutes(organizationFallback, roleId);
    return 480;
  })();

  // Altura dinâmica: Começa abaixo do Header+Ribbon e termina acima do StatusBarGlobal
  // Começa abaixo de Header + Ribbon usando os tokens do shell
  // StatusBarGlobal (h-6 = 24px) na parte inferior
  const sidebarContainerClass = docked
    ? "w-full flex-1 glass-panel dark:bg-slate-900 flex flex-col pointer-events-auto overflow-hidden border-l border-[var(--border-default)]"
    : "fixed right-0 w-80 glass-panel dark:bg-slate-900 dark:border-slate-700 flex flex-col z-[10000] pointer-events-auto overflow-hidden";
  const sidebarStyle = docked ? { borderLeftColor: 'var(--shell-divider)' } : { top: 'calc(var(--shell-header-height, 42px) + var(--shell-ribbon-height, 52px))', bottom: '24px' };

  if (rightPanelMode === 'none') return null;

  // --- MODOS ESPECIAIS ---
  if (rightPanelMode === 'dashboard') {
    const isFullscreen = dashboardMode === 'fullscreen';
    return (
      <div 
        className={isFullscreen ? "fixed right-0 z-[100] bg-white dark:bg-slate-900 flex flex-col" : sidebarContainerClass}
        style={isFullscreen ? { top: 'var(--shell-header-height, 34px)', bottom: '24px', left: 'var(--sidebar-effective-width, var(--sidebar-collapsed-width, 48px))' } : sidebarStyle}
      >
        {/* Header do Dashboard */}
        <div className="h-10 border-b border-slate-300/70 dark:border-slate-700 flex items-center justify-between px-3 shrink-0" style={{ background: 'var(--officebar-bg)', backgroundColor: 'var(--officebar-bg)' }}>
          <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wide flex items-center gap-2">
            <BarChart2 size={14}/> Dashboard
          </h3>
          <div className="flex gap-1">
            <button 
              onClick={onToggleDashboardMode} 
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
              aria-label={dashboardMode === 'sidebar' ? 'Expandir dashboard' : 'Minimizar dashboard'}
              title={dashboardMode === 'sidebar' ? 'Expandir dashboard' : 'Minimizar dashboard'}
            >
              {dashboardMode === 'sidebar' ? <Maximize2 size={14}/> : <Minimize2 size={14}/>}
            </button>
            <button 
              onClick={onClose} 
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Fechar painel"
              title="Fechar painel"
            >
              <X size={16}/>
            </button>
          </div>
        </div>
        {/* Conteúdo do Dashboard */}
        <div className="flex-1 overflow-hidden">
          <DashboardPanel
            metrics={metrics}
            cards={cards}
            onClose={onClose}
            setHighlight={onTogglePathHighlight}
            dashboardMode={dashboardMode}
            onClearMapFilters={onClearMapFilters}
            filterResetToken={dashboardFilterResetToken}
            onSimulateSolution={onSimulateSolution}
            onExpand={onToggleDashboardMode}
            onMinimize={onToggleDashboardMode}
            highlightedPath={highlightedPath}
          />
        </div>
      </div>
    );
  }
  if (rightPanelMode === 'kaizen') {
    return (
      <div className={sidebarContainerClass} style={sidebarStyle}>
        <KaizenLogPanel postIts={postIts} cards={cards} selectedCardId={selectedCardId} selectedPostItId={selectedPostItId} filter={kaizenFilter} onChangeFilter={onKaizenFilterChange} isCollapsed={isKaizenCollapsed} onToggleCollapsed={onToggleKaizenCollapsed} onSelectPostIt={onSelectPostIt} onReorderPostIts={onReorderPostIts} onClearCardFilter={onClearCardFilter} onUpdatePostIt={onUpdatePostIt} onGenerateFutureMap={onGenerateFutureMap} onClose={onClose} onOpenActionsKaizen={onOpenActionsKaizen} onCreateAction={onCreateAction} onOpenActionDetail={onOpenActionDetail} actions={actions} />
      </div>
    );
  }
  if (rightPanelMode === 'yamazumi') {
    return (
      <div className={sidebarContainerClass} style={sidebarStyle}>
        <YamazumiPanel
          isOpen={true}
          isCollapsed={!isExpanded}
          onToggleCollapse={onExpandedChange ? (() => onExpandedChange(!isExpanded)) : undefined}
          onClose={onClose}
          cards={cards}
          assignments={yamazumiAssignments ?? {}}
          shiftMinutes={yamazumiShiftMinutes ?? 480}
          fteByRole={yamazumiFteByRole ?? {}}
          pendingChanges={yamazumiPendingChanges ?? 0}
          onShiftMinutesChange={onYamazumiShiftMinutesChange ?? (() => {})}
          onFteChange={onYamazumiFteChange ?? (() => {})}
          onSyncToMap={onYamazumiSync ?? (() => {})}
          onAutoBalance={onYamazumiAutoBalance}
          onSaveBaseline={onYamazumiSaveBaseline}
          savedBaseline={yamazumiSavedBaseline}
          onSyncToExecution={onYamazumiSyncToExecution}
          syncingToExecution={syncingYamazumiToExecution}
          zoom={yamazumiZoom}
          onZoomChange={onYamazumiZoomChange}
          demandaPercent={yamazumiDemandaPercent}
          onDemandaPercentChange={onYamazumiDemandaPercentChange}
          savedBaselineKpis={yamazumiSavedBaselineKpis}
        />
      </div>
    );
  }
  if (rightPanelMode === 'simulation') return null;

  const selectedCardHasVsmKaizenProjection = Boolean((selectedCard as any)?.vsmKaizenProjection);

  if (rightPanelMode === 'properties' && isSimulationMode && selectedCard && !selectedCardHasVsmKaizenProjection) {
    const isInspectableType = !['card_inicial', 'card_final', 'decisao'].includes(selectedCard.tipo);
    if (isInspectableType) {
      return (
        <div className={sidebarContainerClass} style={sidebarStyle}>
          <KaizenInspector selectedCard={selectedCard} onClose={onClose} />
        </div>
      );
    }
  }

  // --- MODO PROPRIEDADES (DATA ENGINE) ---
  if (!selectedCard) return null;

  const handleInputChange = (field: keyof CardData, value: any) => {
    // Lógica inteligente: Se AC = 100%, zerar TR automaticamente
    if (field === 'percentLivreErros' && value === 100) {
      onUpdateCardData(selectedCard.id, { [field]: value, correcao: 0 });
    } else {
      onUpdateCardData(selectedCard.id, { [field]: value });
    }
  };

  // Lógica de Tempo
  const handleTimeChange = (field: 'tt' | 'lt' | 'correcao', val: number, unit: string) => {
    let finalValueInMinutes = val;
    if (unit === 'x/d' && val > 0) finalValueInMinutes = SHIFT_MINUTES / val;
    else if (unit === 'h') finalValueInMinutes = val * 60;
    else if (unit === 's') finalValueInMinutes = val / 60;
    else if (unit === 'd') finalValueInMinutes = val * SHIFT_MINUTES;
    
    const unitKey = field === 'tt' ? 'customUnitTT' :
                    field === 'lt' ? 'customUnitLT' : 'customUnitTR';
    
    // Atualizar ambos os campos em uma única chamada para evitar problemas de sincronização
    onUpdateCardData(selectedCard.id, { [field]: finalValueInMinutes, [unitKey]: unit } as any);
  };

  const getDisplayValue = (field: 'tt' | 'lt' | 'correcao') => {
    const valInMinutes = (selectedCard[field] as number) || 0;
    const unitKey = field === 'tt' ? 'customUnitTT' :
                    field === 'lt' ? 'customUnitLT' : 'customUnitTR';
    const unit = (selectedCard as any)[unitKey] || 'm';

    if (unit === 'x/d' && valInMinutes > 0) return parseFloat((SHIFT_MINUTES / valInMinutes).toFixed(2));
    if (unit === 'h') return parseFloat((valInMinutes / 60).toFixed(2));
    if (unit === 's') return Math.round(valInMinutes * 60);
    if (unit === 'd') return parseFloat((valInMinutes / SHIFT_MINUTES).toFixed(2));
    return parseFloat(valInMinutes.toFixed(2));
  };

  const toVsmProjectionSeconds = (value: number, unit: string) => {
    if (unit === 'min') return value * 60;
    if (unit === 'h') return value * 3600;
    return value;
  };

  const getKaizenMetricProjection = (metric: 'setup' | 'cycleTime' | 'availability' | 'quality') => {
    return ((selectedCard as any).vsmKaizenProjection?.metrics ?? {})[metric];
  };

  const getProjectedMetricValue = (metric: 'setup' | 'cycleTime' | 'availability' | 'quality', fallback: number) => {
    const projected = Number(getKaizenMetricProjection(metric)?.projectedValue);
    return Number.isFinite(projected) ? projected : fallback;
  };

  const getVsmCycleSeconds = (card: CardData) => {
    const value = Number((card as any).vsmCT ?? 0) || 0;
    const unit = String((card as any).vsmCTUnit ?? 's');
    const cycleSeconds = unit === 'min' ? value * 60 : unit === 'h' ? value * 3600 : value;
    const availability = Number((card as any).vsmDisp);
    if (Number.isFinite(availability) && availability > 0 && availability < 100) {
      return cycleSeconds / (availability / 100);
    }
    return cycleSeconds;
  };

  const selectedFlowCellId = String((selectedCard as any)?.vsmFlowCellId ?? '');
  const selectedFlowCellCards = selectedFlowCellId
    ? allSheetCards.filter(card => (card as any).vsmFlowCellId === selectedFlowCellId && card.tipo === 'atividade' && (card as any).vsmIcon === 'processo')
    : [];
  const selectedFlowCellRawCurrentSeconds = selectedFlowCellCards.reduce((sum, card) => sum + getVsmCycleSeconds(card), 0);
  const selectedFlowCellBaselineSeconds = Number(
    (selectedCard as any)?.vsmKaizenProjection?.flowCellCurrentCycleSeconds
    ?? selectedFlowCellCards.find(card => Number((card as any).vsmFlowCellBaselineCycleSeconds) > 0)?.vsmFlowCellBaselineCycleSeconds
    ?? 0,
  ) || 0;
  const selectedFlowCellProjectedSeconds = Number(
    selectedFlowCellCards.find(card => Number((card as any).vsmFlowCellProjectedCycleSeconds) > 0)?.vsmFlowCellProjectedCycleSeconds
    ?? 0,
  ) || 0;
  const selectedFlowCellLeadTimeReductionPercent = Number(
    selectedFlowCellCards.find(card => Number((card as any).vsmFlowCellLeadTimeReductionPercent) > 0)?.vsmFlowCellLeadTimeReductionPercent
    ?? (selectedCard as any)?.vsmKaizenProjection?.flowCellLeadTimeReductionPercent
    ?? 0,
  ) || 0;
  const selectedFlowCellCurrentSeconds = selectedFlowCellBaselineSeconds > 0
    ? Math.max(selectedFlowCellBaselineSeconds, selectedFlowCellRawCurrentSeconds)
    : selectedFlowCellRawCurrentSeconds;
  const selectedKaizenIsFlowCellScoped = Boolean(
    (selectedCard as any)?.vsmKaizenProjection?.scope === 'flowCell'
    || (selectedCard as any)?.vsmKaizenProjection?.flowCellId
    || ((selectedCard as any)?.vsmFlowCellKaizenLayout && (selectedCard as any)?.vsmKaizenProjection),
  );

  const updateSelectedFlowCellProjection = (projectedCycleSeconds: number) => {
    if (!selectedFlowCellId) return;
    const baselineSeconds = Math.max(selectedFlowCellCurrentSeconds, selectedFlowCellRawCurrentSeconds);
    setCards(allSheetCards.map(card => (card as any).vsmFlowCellId === selectedFlowCellId
      ? {
          ...card,
          vsmFlowCellBaselineCycleSeconds: (card as any).vsmFlowCellBaselineCycleSeconds ?? baselineSeconds,
          vsmFlowCellProjectedCycleSeconds: Math.max(0, projectedCycleSeconds),
          vsmKaizenProjection: card.id === selectedCard.id
            ? {
                ...((card as any).vsmKaizenProjection ?? {}),
                scope: 'flowCell',
                flowCellId: selectedFlowCellId,
                type: 'cycleTime',
                improvementType: 'cycleTime',
                flowCellCurrentCycleSeconds: baselineSeconds,
                flowCellProjectedCycleSeconds: Math.max(0, projectedCycleSeconds),
              }
            : (card as any).vsmKaizenProjection,
        } as CardData
      : card));
  };

  const updateSelectedFlowCellLeadTimeReduction = (reductionPercent: number) => {
    if (!selectedFlowCellId) return;
    const normalizedPercent = Math.max(0, Math.min(100, Number(reductionPercent) || 0));
    setCards(allSheetCards.map(card => (card as any).vsmFlowCellId === selectedFlowCellId
      ? {
          ...card,
          vsmFlowCellLeadTimeReductionPercent: normalizedPercent,
          vsmKaizenProjection: card.id === selectedCard.id
            ? {
                ...((card as any).vsmKaizenProjection ?? {}),
                scope: 'flowCell',
                flowCellId: selectedFlowCellId,
                type: 'layout',
                improvementType: 'layout',
                flowCellLeadTimeReductionPercent: normalizedPercent,
              }
            : (card as any).vsmKaizenProjection,
        } as CardData
      : card));
  };

  const updateKaizenMetricProjection = (
    metric: 'setup' | 'cycleTime' | 'availability' | 'quality',
    currentValue: number,
    projectedValue: number,
    unit: string,
  ) => {
    const previousProjection = (selectedCard as any).vsmKaizenProjection ?? {};
    const projectedSeconds = metric === 'cycleTime'
      ? toVsmProjectionSeconds(projectedValue, unit)
      : metric === 'setup'
        ? projectedValue * 60
        : undefined;
    const nextMetrics = {
      ...(previousProjection.metrics ?? {}),
      [metric]: {
        currentValue,
        projectedValue,
        unit,
        projectedSeconds,
      },
    };

    onUpdateCardData(selectedCard.id, {
      vsmKaizenProjection: {
        ...previousProjection,
        type: nextMetrics.cycleTime ? 'cycleTime' : 'multiMetric',
        currentValue: nextMetrics.cycleTime?.currentValue,
        projectedValue: nextMetrics.cycleTime?.projectedValue,
        projectedSeconds: nextMetrics.cycleTime?.projectedSeconds,
        metrics: nextMetrics,
      },
    } as any);

    simulation?.setIsActive?.(true);
    simulation?.setIsSimulationActive?.(true);
    if (metric === 'cycleTime') {
      const currentSeconds = toVsmProjectionSeconds(currentValue, unit);
      const reductionPercent = currentSeconds > 0 && projectedSeconds !== undefined
        ? Math.max(0, Math.min(100, ((currentSeconds - projectedSeconds) / currentSeconds) * 100))
        : 0;
      simulation?.setKaizenOverride?.(selectedCard.id, {
        ...(simulation?.kaizenOverrides?.[selectedCard.id] ?? {}),
        rework: reductionPercent,
        reworkReduction: reductionPercent,
        gainPercent: reductionPercent,
        targetMetric: 'cycleTime',
        timeOverride: projectedSeconds,
        timeOverrideSeconds: projectedSeconds,
        kaizenType: 'multiMetric',
        metrics: nextMetrics,
      } as any);
    }
  };

  const updateKaizenProjectionTitle = (title: string) => {
    const previousProjection = (selectedCard as any).vsmKaizenProjection ?? {};
    onUpdateCardData(selectedCard.id, {
      vsmKaizenProjection: {
        ...previousProjection,
        title,
      },
    } as any);
  };

  const updateKaizenProjectionType = (improvementType: string) => {
    const previousProjection = (selectedCard as any).vsmKaizenProjection ?? {};
    onUpdateCardData(selectedCard.id, {
      vsmKaizenProjection: {
        ...previousProjection,
        improvementType,
      },
    } as any);
  };

  const removeKaizenProjection = () => {
    const { vsmKaizenProjection, ...cleanCard } = selectedCard as any;
    onUpdateCardData(selectedCard.id, cleanCard as Partial<CardData>);
    const nextOverrides = { ...(simulation?.kaizenOverrides ?? {}) } as Record<string, any>;
    delete nextOverrides[selectedCard.id];
    simulation?.setKaizenOverrides?.(nextOverrides);
  };

  // --- RECURSOS (CORRIGIDO PARA TEXTO) ---
  const resources: ResourceItem[] = (selectedCard as any).resources || [];

  const addResource = () => {
    const newResources = [...resources, { role: '', quantity: 1 }];
    const totalFTE = newResources.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    const quemString = newResources.map(r => r.role || '').filter(Boolean).join(', ');
    onUpdateCardData(selectedCard.id, { resources: newResources, pessoasSimultaneas: totalFTE, quem: quemString } as any);
  };

  const updateResource = (index: number, field: keyof ResourceItem, value: any) => {
    const newResources = [...resources];
    newResources[index] = { ...newResources[index], [field]: value };
    const totalFTE = newResources.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    const quemString = newResources.map(r => r.role || '').filter(Boolean).join(', ');
    onUpdateCardData(selectedCard.id, { resources: newResources, pessoasSimultaneas: totalFTE, quem: quemString } as any);
  };

  const removeResource = (index: number) => {
    const newResources = resources.filter((_, i) => i !== index);
    const totalFTE = newResources.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    const quemString = newResources.map(r => r.role || '').filter(Boolean).join(', ');
    onUpdateCardData(selectedCard.id, { resources: newResources, pessoasSimultaneas: totalFTE, quem: quemString } as any);
  };

  // Estilos
  const sectionClass = "px-4 py-3 border-b border-slate-200 dark:border-slate-700";
  // V229: padrão moderno — sem uppercase, peso semibold, contraste mais suave.
  // Reduz ruído visual no painel direito sem perder hierarquia.
  const labelClass = "text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1 block";
  const inputClass = "w-full h-8 px-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-700 rounded text-xs font-semibold text-slate-900 dark:text-slate-100 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all placeholder-slate-400 dark:placeholder-slate-500";
  const selectClass = "h-8 px-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-700 rounded text-xs font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:border-blue-400 cursor-pointer";

  // Obter/criar executionConfig
  const executionConfig: ExecutionConfig = selectedCard.executionConfig || {
    fields: [],
    actionType: 'simple_complete'
  };
  
  // Handlers do Form Builder
  const handleAddField = (type: ExecutionFieldType) => {
    const newField: ExecutionField = {
      id: `field-${Date.now()}`,
      type,
      label: getDefaultLabel(type),
      required: false
    };
    const updatedConfig: ExecutionConfig = {
      ...executionConfig,
      fields: [...executionConfig.fields, newField]
    };
    onUpdateCardData(selectedCard.id, { executionConfig: updatedConfig });
    setShowFieldTypeMenu(false);
  };
  
  const handleUpdateField = (fieldId: string, updates: Partial<ExecutionField>) => {
    const updatedFields = executionConfig.fields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    );
    onUpdateCardData(selectedCard.id, { 
      executionConfig: { ...executionConfig, fields: updatedFields } 
    });
  };
  
  const handleRemoveField = (fieldId: string) => {
    const updatedFields = executionConfig.fields.filter(f => f.id !== fieldId);
    onUpdateCardData(selectedCard.id, { 
      executionConfig: { ...executionConfig, fields: updatedFields } 
    });
  };
  
  const handleUpdateExecutionConfig = (updates: Partial<ExecutionConfig>) => {
    onUpdateCardData(selectedCard.id, { 
      executionConfig: { ...executionConfig, ...updates } 
    });
  };
  
  // Helper para label padrão de cada tipo
  const getDefaultLabel = (type: ExecutionFieldType): string => {
    const labels: Record<ExecutionFieldType, string> = {
      text: 'Campo de Texto',
      number: 'Valor Numérico',
      date: 'Data',
      checkbox: 'Verificação',
      select: 'Seleção',
      currency: 'Valor (R$)',
      attachment: 'Anexo',
      textarea: 'Texto Longo'
    };
    return labels[type];
  };
  
  // Ícone por tipo de campo
  const getFieldIcon = (type: ExecutionFieldType) => {
    const icons: Record<ExecutionFieldType, React.ReactNode> = {
      text: <Type size={12} />,
      number: <Hash size={12} />,
      date: <Calendar size={12} />,
      checkbox: <CheckSquare size={12} />,
      select: <List size={12} />,
      currency: <span className="text-[10px] font-bold">R$</span>,
      attachment: <Paperclip size={12} />,
      textarea: <AlignLeft size={12} />
    };
    return icons[type];
  };
  
  // Tipos disponíveis no Form Builder
  const fieldTypes: { type: ExecutionFieldType; label: string }[] = [
    { type: 'checkbox', label: 'Checkbox' },
    { type: 'text', label: 'Texto' },
    { type: 'number', label: 'Número' },
    { type: 'currency', label: 'Valor (R$)' },
    { type: 'date', label: 'Data' },
    { type: 'select', label: 'Seleção' },
    { type: 'textarea', label: 'Texto Longo' },
    { type: 'attachment', label: 'Anexo' },
  ];

  // Verificar se é um tipo de nó que suporta execução
  const supportsExecution = ['atividade', 'decisao'].includes(selectedCard.tipo);

  // Quando vinculado a um blueprint publicado E corporativo, execução é somente-leitura
  // Mapas abertos para edição devem permitir configurar executionConfig
  const isExecutionReadOnly = false;

  return (
    <div className={sidebarContainerClass} style={sidebarStyle}>
      {/* Header */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-slate-300/70 dark:border-slate-700 shrink-0" style={{ background: 'var(--officebar-bg)', backgroundColor: 'var(--officebar-bg)' }}>
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Settings size={14} className="text-blue-600 dark:text-blue-400" />
          <span className="font-black text-xs uppercase tracking-wider">Propriedades</span>
        </div>
        <div className="flex items-center gap-1">
          {supportsExecution && activeTab === 'execution' && onExpandedChange && (
            <button
              onClick={() => onExpandedChange(!isExpanded)}
              className="text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-black/5 dark:hover:bg-slate-700 p-1 rounded transition-colors"
              title={isExpanded ? 'Compactar painel' : 'Expandir painel'}
            >
              {isExpanded ? <Shrink size={13} /> : <Expand size={13} />}
            </button>
          )}
          <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-red-600 hover:bg-black/5 dark:hover:bg-slate-700 p-1 rounded transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tab Switcher — only for activity/decision cards */}
      {supportsExecution && (
        <div className="flex border-b border-slate-200 dark:border-slate-700 shrink-0 bg-white dark:bg-slate-800">
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              activeTab === 'metrics'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            Métricas
          </button>
          <button
            onClick={() => setActiveTab('execution')}
            className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
              activeTab === 'execution'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            Execução
          </button>
        </div>
      )}

      {/* V154.0 → V228: bloco de Configuração Global da Fábrica extraído para
           src/components/GlobalFactorySettings.tsx (componente standalone, ainda nao
           renderizado no painel de cards — será posicionado na Officebar). */}

      <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-800">

        {/* ═══ EXECUTION TAB ═══ */}
        {activeTab === 'execution' && supportsExecution && (
          <ExecutionTabContent
            executionConfig={executionConfig}
            isExecutionReadOnly={isExecutionReadOnly}
            isExpanded={isExpanded}
            onUpdateExecutionConfig={handleUpdateExecutionConfig}
            sectionClass={sectionClass}
            labelClass={labelClass}
            inputClass={inputClass}
          />
        )}

        {/* ═══ METRICS TAB (original content) ═══ */}
        {(activeTab === 'metrics' || !supportsExecution) && (
        <>
        {selectedCard.tipo === 'decisao' ? (
          /* MODO DECISÃO */
          <div className={sectionClass}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Pergunta de Decisão</label>
                <BufferedTextInput
                  value={selectedCard.atividade || ''}
                  onCommit={(val) => handleInputChange('atividade', val)}
                  className={inputClass + " font-bold border-l-4 border-l-purple-500"}
                  placeholder="Ex: Aprovado?"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass + " text-green-600"}>Rótulo SIM (Direita)</label>
                  <BufferedTextInput
                    value={selectedCard.decisao_sim_label || 'SIM'}
                    onCommit={(val) => handleInputChange('decisao_sim_label', val)}
                    className={inputClass}
                    placeholder="SIM"
                  />
                </div>
                <div>
                  <label className={labelClass + " text-red-600"}>Rótulo NÃO (Baixo)</label>
                  <BufferedTextInput
                    value={selectedCard.decisao_nao_label || 'NÃO'}
                    onCommit={(val) => handleInputChange('decisao_nao_label', val)}
                    className={inputClass}
                    placeholder="NÃO"
                  />
                </div>
              </div>

              <div className="bg-slate-100 dark:bg-slate-700 p-3 rounded border border-slate-200 dark:border-slate-700">
                <label className={labelClass + " mb-2"}>Distribuição de Fluxo (%)</label>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[10px] font-bold text-green-700 dark:text-green-500">Caminho SIM</span>
                      <span className="text-xs font-black dark:text-slate-200">{selectedCard.decisao_sim_pct || 50}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={selectedCard.decisao_sim_pct || 50}
                      onChange={(e) => {
                        const sim = parseInt(e.target.value);
                        onUpdateCardData(selectedCard.id, {
                          decisao_sim_pct: sim,
                          decisao_nao_pct: 100 - sim
                        });
                      }}
                      className="w-full h-1.5 bg-slate-300 dark:bg-slate-600 rounded appearance-none cursor-pointer accent-green-600"
                    />
                  </div>
                  <div className="pt-1 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-red-700 dark:text-red-500">Caminho NÃO (Auto)</span>
                      <span className="text-xs font-black text-slate-500 dark:text-slate-400">{selectedCard.decisao_nao_pct || 50}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* MODO ATIVIDADE / OUTROS */
          <>
            {/* 1. Identificação */}
            <div className={selectedKaizenIsFlowCellScoped ? 'hidden' : sectionClass}>
              <div className="space-y-3">
                {selectedCard.tipo === 'card_inicial' ? (
                  /* CAMPOS EXCLUSIVOS CARD INICIAL */
                  <div className="space-y-4">
                    {selectedCard.mapOrigin === 'FACTORY' ? (
                      /* FACTORY: Cliente / Fornecedor */
                      <div>
                          <label className={labelClass}>Tipo de Entidade Externa</label>
                          <select 
                            className={"w-full h-8 pl-3 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"}
                            value={(selectedCard as any).extType || 'CLIENTE'}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleInputChange('extType' as any, val);
                              handleInputChange('atividade', val);
                            }}
                          >
                            <option value="CLIENTE">CLIENTE</option>
                            <option value="FORNECEDOR">FORNECEDOR</option>
                          </select>
                        </div>
                    ) : (
                      /* OFFICE: fluxo genérico */
                      <>
                        <div>
                          <label className={labelClass}>Nome do Fluxo</label>
                          <BufferedTextInput
                            value={selectedCard.nome_fluxo || ''}
                            onCommit={(val) => handleInputChange('nome_fluxo', val)}
                            className={inputClass + " font-bold border-l-4 border-l-emerald-500"}
                            placeholder="Ex: Processo de Vendas"
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Demanda</label>
                          <div className="relative w-full">
                            <input
                              type="number"
                              value={selectedCard.entrada || 0}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                handleInputChange('entrada', val);
                              }}
                              className="w-full h-8 pl-3 pr-[75px] rounded-sm border border-emerald-300 dark:border-emerald-500/50 text-right text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                              placeholder="100"
                              step="1"
                              min="0"
                              onFocus={(e) => e.target.select()}
                            />
                            <select 
                              className="absolute right-0 top-0 h-8 w-[70px] bg-transparent dark:bg-transparent border-none text-[10px] font-bold text-emerald-700 dark:text-emerald-400 focus:outline-none cursor-pointer appearance-none text-center pr-2"
                              value={(selectedCard as any).unidadeDemanda || '/dia'}
                              onChange={(e) => handleInputChange('unidadeDemanda' as any, e.target.value)}
                              style={{backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", backgroundSize: "12px"}}
                            >
                              <option value="/dia">/dia</option>
                              <option value="/hora">/hora</option>
                              <option value="/mês">/mês</option>
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : selectedCard.tipo === 'card_final' ? (
                  /* CAMPOS EXCLUSIVOS CARD FINAL */
                  <div className="space-y-4">
                    {selectedCard.mapOrigin === 'FACTORY' && ['externo','fornecedor','cliente'].includes(selectedCard.vsmIcon || '') && (
                      <div>
                        <label className={labelClass}>Tipo de Entidade Externa</label>
                        <select
                          className={"w-full h-8 pl-3 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"}
                          value={(selectedCard as any).extType || 'CLIENTE'}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleInputChange('extType' as any, val);
                            handleInputChange('atividade', val);
                          }}
                        >
                          <option value="CLIENTE">CLIENTE</option>
                          <option value="FORNECEDOR">FORNECEDOR</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>Nome do Fluxo</label>
                      <BufferedTextInput
                        value={selectedCard.nome_fluxo || ''}
                        onCommit={(val) => handleInputChange('nome_fluxo', val)}
                        className={inputClass + " font-bold border-l-4 border-l-slate-500"}
                        placeholder="Ex: Processo de Vendas"
                      />
                    </div>
                  </div>
                ) : (
                  /* CAMPOS PADRÃO ATIVIDADE */
                  <>
                    {/* V228: Seletor de subtipo de card (Processo / Expedição).
                        Era visualmente um Tab — anti-pattern para seleção de
                        propriedade de dado. Convertido para <select> claro. */}
                    {mapType === 'FACTORY' && selectedCard.vsmIcon === 'processo' && (
                      <div className="mb-1">
                        <label className={labelClass} htmlFor="rp-vsm-subtype">Tipo de Card</label>
                        <select
                          id="rp-vsm-subtype"
                          value={(selectedCard as any).vsmSubtype === 'expedition' ? 'expedition' : 'process'}
                          onChange={(e) => {
                            const isExpedition = e.target.value === 'expedition';
                            if (isExpedition) {
                              onUpdateCardData(selectedCard.id, {
                                vsmSubtype: 'expedition',
                                atividade: (selectedCard as any).atividade || 'EXPEDIÇÃO',
                              } as any);
                            } else {
                              onUpdateCardData(selectedCard.id, { vsmSubtype: undefined } as any);
                            }
                          }}
                          className="w-full h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        >
                          <option value="process">Processo</option>
                          <option value="expedition">Expedição</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>{selectedKaizenIsFlowCellScoped ? 'Âncora técnica da célula' : mapType === 'FACTORY' && ['pcpm','nivelamento','info_eletro'].includes(selectedCard.vsmIcon || '') ? 'Nome' : mapType === 'FACTORY' ? 'Processo / Posto' : 'Atividade'}</label>
                      <BufferedTextInput
                        value={selectedCard.atividade || selectedCard.nome_fluxo || ''}
                        onCommit={(val) => handleInputChange(selectedCard.tipo === 'card_final' ? 'nome_fluxo' : 'atividade', val)}
                        className={inputClass + " font-bold border-l-4 border-l-blue-500"}
                        placeholder="Nome da etapa..."
                      />
                    </div>
                    {mapType !== 'FACTORY' && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className={labelClass}>Como (Sistema)</label>
                        <BufferedTextInput
                          value={(selectedCard as any).sistema}
                          onCommit={(val) => onUpdateCardData(selectedCard.id, { sistema: val } as any)}
                          className={inputClass}
                          placeholder="Ex: SAP"
                          list="systems-list"
                        />
                        <datalist id="systems-list">
                          {COMMON_SYSTEMS.map(sys => <option key={sys} value={sys} />)}
                        </datalist>
                      </div>
                      <div className="flex-1">
                        <label className={labelClass}>Departamento</label>
                        <CreatableDepartmentField
                          value={(selectedCard as any).setor || (selectedCard as any).sector || ''}
                          officialDepts={orgDepartments.length > 0 ? orgDepartments : (organizationFallback?.departments ?? [])}
                          suggestions={sectorSuggestions}
                          onChange={(val) => {
                            const dept = orgDepartments.find(d => d.name === val) || organizationFallback?.departments?.find(d => d.name === val);
                            if (!dept && val) addSectorSuggestion(val);
                            onUpdateCardData(selectedCard.id, { setor: val, sector: val, department_id: dept?.id ?? null } as any);
                          }}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 2. Recursos (Lista Dinâmica) */}
            {mapType !== 'FACTORY' && selectedCard.tipo !== 'decisao' && selectedCard.tipo !== 'card_inicial' && selectedCard.tipo !== 'card_final' && (
            <div className={sectionClass}>
              <div className="flex justify-between items-center mb-2">
                <label className={labelClass + " flex items-center gap-1 text-slate-700"}><Users size={12}/> Recursos</label>
                <button onClick={addResource} className="text-[9px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-0.5 rounded flex items-center gap-1 transition-colors border border-blue-100 hover:border-blue-300">
                  <Plus size={10} /> Add
                </button>
              </div>
              
              <div className="space-y-2 max-h-[150px] overflow-y-auto custom-scrollbar pr-1">
                {resources.length === 0 && (
                  <div className="text-xs text-slate-400 dark:text-slate-500 italic text-center py-2 border border-dashed border-slate-200 dark:border-slate-700 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" onClick={addResource}>
                    + Adicionar Quem/Qtd
                  </div>
                )}
                
                {resources.map((res, idx) => {
                  // Buscar departamento selecionado e filtrar cargos (DB-backed com fallback in-memory)
                  const selectedDepartment = (selectedCard as any).setor || (selectedCard as any).sector || '';
                  const deptFromDb = orgDepartments.find(d => d.name === selectedDepartment);
                  const deptFromMemory = organizationFallback?.departments?.find(d => d.name === selectedDepartment);
                  const department = deptFromDb || deptFromMemory;
                  const availableRoles = department
                    ? (deptFromDb 
                        ? getRolesForDepartment(department.id)
                        : organizationFallback?.roles?.filter(r => r.departmentId === department.id && r.visible !== false) || [])
                    : [];
                  
                  return (
                  <div key={idx} className="flex gap-1 items-center animate-in fade-in slide-in-from-left-1 duration-200">
                    <div className="relative flex-1">
                      <Briefcase size={10} className="absolute left-2 top-2.5 text-slate-400 pointer-events-none z-10" />
                      {selectedDepartment && availableRoles.length > 0 ? (
                        <select
                          value={res.role}
                          onChange={(e) => updateResource(idx, 'role', e.target.value)}
                          className={inputClass + " pl-6 h-8 text-[11px] appearance-none"}
                          disabled={!selectedDepartment}
                        >
                          <option value="">Selecione o cargo...</option>
                          {availableRoles.map(role => (
                            <option key={role.id} value={role.name}>{role.name}</option>
                          ))}
                        </select>
                      ) : (
                        <BufferedTextInput
                          value={res.role}
                          onCommit={(val) => updateResource(idx, 'role', val)}
                          className={inputClass + " pl-6 h-8 text-[11px]"}
                          placeholder={selectedDepartment ? "Nenhum cargo cadastrado" : "Selecione o departamento primeiro"}
                        />
                      )}
                    </div>
                    <div className="w-14">
                      <BufferedInput
                        value={res.quantity}
                        onCommit={(val) => updateResource(idx, 'quantity', val)}
                        className={inputClass + " h-8 text-center text-[11px] px-1"}
                        step={0.5}
                        min={0}
                      />
                    </div>
                    <button 
                      onClick={() => removeResource(idx)}
                      className="text-slate-300 hover:text-red-500 p-1 hover:bg-red-50 rounded"
                      title="Remover"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
            )}
          </>
        )}

        {/* 3. Tempos e Qualidade - Grid Simétrico */}
        {selectedCard.tipo !== 'decisao' && selectedCard.tipo !== 'card_inicial' && selectedCard.tipo !== 'card_final'
          && !(mapType === 'FACTORY' && selectedCard.vsmIcon === 'processo')
          && !(mapType === 'FACTORY' && ['pcpm','nivelamento','info_eletro'].includes(selectedCard.vsmIcon || ''))
          && !(mapType === 'FACTORY' && (['estoque','pulmao','supermercado','fifo'].includes(selectedCard.vsmIcon || '') || selectedCard.tipo === 'inventory'))
          && !(mapType === 'FACTORY' && ['fornecedor','cliente','externo'].includes(selectedCard.vsmIcon || ''))
          && !(mapType === 'FACTORY' && selectedCard.vsmIcon === 'comunicacao') && (
        <div className={sectionClass}>
          <label className={labelClass + " mb-3 flex items-center gap-1 text-blue-700"}><Clock size={12}/> Tempos do Processo</label>
          
          {/* Layout Vertical para TP e TRA */}
          <div className="space-y-3 mb-4">
            {/* TP - Lead Time - Joined Input Group */}
            <div>
              <label className={labelClass}>{mapType === 'FACTORY' ? 'LT | Lead Time' : 'TP | Tempo de Permanência'}</label>
              <div className="relative w-full">
                <input
                  type="number"
                  value={getDisplayValue('lt')}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    handleTimeChange('lt', val, (selectedCard as any).customUnitLT || 'm');
                  }}
                  className="w-full h-8 pl-3 pr-[75px] rounded-sm border border-blue-300 dark:border-blue-500/50 text-right text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  placeholder="0"
                  step="0.1"
                  min="0"
                  onFocus={(e) => e.target.select()}
                />
                <select 
                  className="absolute right-0 top-0 h-8 w-[70px] bg-transparent dark:bg-transparent border-none text-[10px] font-bold text-blue-700 dark:text-blue-400 focus:outline-none cursor-pointer appearance-none text-center pr-2"
                  value={(selectedCard as any).customUnitLT || 'm'} 
                  onChange={(e) => handleTimeChange('lt', getDisplayValue('lt'), e.target.value)}
                  style={{backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", backgroundSize: "12px"}}
                >
                  <option value="s">seg</option>
                  <option value="m">min</option>
                  <option value="h">h</option>
                  <option value="d">dia</option>
                  <option value="x/d">vezes/dia</option>
                </select>
              </div>
            </div>

            {/* TRA - Cycle Time - Joined Input Group */}
            <div>
              <label className={labelClass}>{mapType === 'FACTORY' ? 'TAV | Tempo de Agregação de Valor' : 'TRA | Tempo de Realização'}</label>
              <div className="relative w-full">
                <input
                  type="number"
                  value={getDisplayValue('tt')}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    handleTimeChange('tt', val, (selectedCard as any).customUnitTT || 'm');
                  }}
                  className="w-full h-8 pl-3 pr-[75px] rounded-sm border border-green-300 dark:border-green-500/50 text-right text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  placeholder="0"
                  step="0.1"
                  min="0"
                  onFocus={(e) => e.target.select()}
                />
                <select 
                  className="absolute right-0 top-0 h-8 w-[70px] bg-transparent dark:bg-transparent border-none text-[10px] font-bold text-green-700 dark:text-green-400 focus:outline-none cursor-pointer appearance-none text-center pr-2"
                  value={(selectedCard as any).customUnitTT || 'm'} 
                  onChange={(e) => handleTimeChange('tt', getDisplayValue('tt'), e.target.value)}
                  style={{backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", backgroundSize: "12px"}}
                >
                  <option value="s">seg</option>
                  <option value="m">min</option>
                  <option value="h">h</option>
                </select>
              </div>
            </div>
          </div>

          {/* Shadow Metric: Espera Implícita */}
          {(() => {
            const tp = selectedCard.lt || 0;
            const tra = selectedCard.tt || 0;
            const espera = tp - tra;
            const esperaPercent = tp > 0 ? (espera / tp) * 100 : 0;
            
            if (espera > 0.5) {
              return (
                <div className="flex items-center gap-1 mb-4 px-2 py-1.5 bg-slate-50 dark:bg-slate-700 rounded text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                  <Clock size={11} className="opacity-60" />
                  <span>Espera: <b className="text-slate-700 dark:text-slate-300">{espera.toFixed(1)} min</b> <span className="text-slate-400 dark:text-slate-500">({esperaPercent.toFixed(0)}%)</span></span>
                </div>
              );
            }
            return null;
          })()}

          {/* Divisor */}
          <div className="border-t border-slate-200 pt-3 mt-3">
            <label className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase mb-3 flex items-center gap-1">
              <Target size={11}/> Qualidade
            </label>

            {/* Layout Vertical AC/TR com Divulgação Progressiva */}
            {(() => {
              const ac = (selectedCard as any).percentLivreErros ?? 100;
              const needsRework = ac < 100;
              const trValue = getDisplayValue('correcao');
              
              return (
                <div className="space-y-3">
                  {/* AC - Assertividade */}
                  <div>
                    <label className={labelClass}>AC | Assertividade (%)</label>
                    <div className="relative h-9">
                      <BufferedInput
                        value={ac}
                        onCommit={(val) => handleInputChange('percentLivreErros', val)}
                        className={inputClass + " pr-8 text-right font-bold h-full"}
                        min={0}
                        max={100}
                        placeholder="100"
                      />
                      <span className="absolute right-3 top-2.5 text-[10px] font-bold text-slate-400">%</span>
                    </div>
                  </div>

                  {/* TR - Retrabalho (Aparece apenas se AC < 100) - Joined Input Group */}
                  {needsRework && (
                    <div className="animate-fade-in">
                      <label className={labelClass}>TR | Tempo de Retrabalho</label>
                      <div className="relative w-full">
                        <input
                          type="number"
                          value={trValue}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            handleTimeChange('correcao', val, (selectedCard as any).customUnitTR || 'm');
                          }}
                          className="w-full h-8 pl-3 pr-[75px] rounded-sm border border-red-300 text-right text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                          placeholder="0"
                          step="0.1"
                          min="0"
                          onFocus={(e) => e.target.select()}
                        />
                        <select 
                          className="absolute right-0 top-0 h-8 w-[70px] bg-transparent border-none text-[10px] font-bold text-red-700 focus:outline-none cursor-pointer appearance-none text-center pr-2"
                          value={(selectedCard as any).customUnitTR || 'm'} 
                          onChange={(e) => handleTimeChange('correcao', getDisplayValue('correcao'), e.target.value)}
                          style={{backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", backgroundSize: "12px"}}
                        >
                          <option value="s">seg</option>
                          <option value="m">min</option>
                          <option value="h">h</option>
                        </select>
                      </div>
                      {trValue === 0 && (
                        <div className="mt-1.5 text-[10px] text-orange-600 flex items-center gap-1 animate-fade-in">
                          <span>⚠️</span>
                          <span>Defina o tempo de retrabalho</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

        </div>
        )}

        {selectedKaizenIsFlowCellScoped && (
          <div className={sectionClass} style={{ order: -1 }}>
            <div className="rounded-sm border border-cyan-200/70 bg-cyan-50/60 p-2 dark:border-cyan-500/30 dark:bg-cyan-950/20">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-800 dark:text-cyan-200">Kaizen da Célula</span>
                <button
                  type="button"
                  onClick={removeKaizenProjection}
                  className="inline-flex items-center gap-1 rounded-sm border border-red-300/70 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-950/30"
                >
                  <Trash2 size={10} /> Remover
                </button>
              </div>
              <div className="mb-2">
                <label className={labelClass}>Título Kaizen</label>
                <BufferedTextInput
                  value={(selectedCard as any).vsmKaizenProjection?.title ?? ''}
                  onCommit={updateKaizenProjectionTitle}
                  className={inputClass}
                  placeholder="Ex: Layout, Ciclo, Balanceamento"
                />
              </div>
              <div className="mb-2">
                <label className={labelClass}>Tipo Kaizen</label>
                <select
                  value={(selectedCard as any).vsmKaizenProjection?.improvementType ?? 'layout'}
                  onChange={(e) => updateKaizenProjectionType(e.target.value)}
                  className="w-full h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                >
                  <option value="cycleTime">Redução de T/C</option>
                  <option value="setup">Setup/SMED</option>
                  <option value="quality">Qualidade</option>
                  <option value="tpm">TPM / Disponibilidade</option>
                  <option value="layout">Layout / Célula</option>
                </select>
              </div>
              {(selectedCard as any).vsmKaizenProjection?.improvementType === 'layout' ? (
                <div className="rounded-sm border border-cyan-300/60 bg-cyan-50/70 p-2 dark:border-cyan-500/30 dark:bg-cyan-950/20">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-800 dark:text-cyan-200">Redução de Lead Time da Célula</span>
                    <span className="truncate text-[9px] font-semibold uppercase tracking-wide text-cyan-600/80 dark:text-cyan-300/80">Âncora no header</span>
                  </div>
                  <div>
                    <label className={labelClass}>Redutor de LT / NVA</label>
                    <div className="relative w-full">
                      <BufferedInput
                        value={selectedFlowCellLeadTimeReductionPercent}
                        onCommit={updateSelectedFlowCellLeadTimeReduction}
                        className={inputClass + " pr-6 text-right"}
                        min={0}
                        max={100}
                        step="1"
                      />
                      <span className="absolute right-2 top-2 text-[10px] text-slate-400">%</span>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="rounded-sm border border-cyan-300/60 bg-cyan-50/70 p-2 dark:border-cyan-500/30 dark:bg-cyan-950/20">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-800 dark:text-cyan-200">Work Content Override</span>
                  <span className="truncate text-[9px] font-semibold uppercase tracking-wide text-cyan-600/80 dark:text-cyan-300/80">{selectedFlowCellCards.length} processos</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelClass}>T/C Total Atual</label>
                    <input
                      readOnly
                      value={`${Math.round(selectedFlowCellCurrentSeconds)} s`}
                      className={inputClass + " text-right opacity-75"}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>T/C Total Projetado</label>
                    <div className="relative w-full">
                      <BufferedInput
                        value={selectedFlowCellProjectedSeconds || Math.round(selectedFlowCellCurrentSeconds)}
                        onCommit={updateSelectedFlowCellProjection}
                        className={inputClass + " pr-6 text-right"}
                        min={0}
                        step="1"
                      />
                      <span className="absolute right-2 top-2 text-[10px] text-slate-400">s</span>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {/* VSM FACTORY — Campos específicos por tipo de card */}
        {mapType === 'FACTORY' && !selectedKaizenIsFlowCellScoped && selectedCard && selectedCard.tipo !== 'decisao' && selectedCard.tipo !== 'card_inicial' && (selectedCard.tipo !== 'card_final' || (['fornecedor','cliente','externo'].includes(selectedCard.vsmIcon || ''))) && (
        <div className={sectionClass} style={{ order: -1 }}>
          <label className={labelClass + " mb-3 flex items-center gap-1 text-emerald-700"}>
            <span style={{fontSize:10}}>◈</span>{' '}
            {selectedCard.vsmIcon === 'processo' ? 'Dados VSM'
              : selectedCard.vsmIcon === 'nivelamento' ? 'Nivelamento / Heijunka'
              : ['pcpm','info_eletro'].includes(selectedCard.vsmIcon || '') ? 'PCPM'
              : (selectedCard.tipo === 'inventory' || ['estoque','pulmao','supermercado','fifo'].includes(selectedCard.vsmIcon || '')) ? 'Dados de Inventário'
              : ['fornecedor','cliente','externo'].includes(selectedCard.vsmIcon || '') ? 'Planejamento / Demanda'
              : selectedCard.vsmIcon === 'comunicacao' ? 'Parâmetros Técnicos'
              : 'Dados VSM'}
          </label>
          {/* PROCESSO: campos condicionais */}
          {/* PCPM / MRP: campo de método */}
          {['pcpm','info_eletro'].includes(selectedCard.vsmIcon || '') && (
            <div className="space-y-2">
              <div>
                <label className={labelClass}>Tipo de Programação</label>
                <input
                  type="text"
                  value={(selectedCard as any).vsmMetodo ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmMetodo: e.target.value } as any)}
                  className={inputClass}
                  placeholder="Ex: MRP, ERP, Kanban…"
                />
              </div>
              <div className="flex items-center justify-between rounded-sm border border-slate-200 dark:border-slate-600 px-3 py-2">
                <div>
                  <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Sistema Puxado</span>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{(selectedCard as any).vsmLocked ? 'MRP bloqueado — fluxo puxado ativo' : 'MRP ativo — fluxo empurrado'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onUpdateCardData(selectedCard.id, { vsmLocked: !(selectedCard as any).vsmLocked } as any)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${(selectedCard as any).vsmLocked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${(selectedCard as any).vsmLocked ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          )}
          {/* NIVELAMENTO / HEIJUNKA */}
          {selectedCard.vsmIcon === 'nivelamento' && (() => {
            const PALETTE = ['#3b82f6','#ef4444','#f59e0b','#10b981','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
            const mixItems: Array<{label:string;qty:number;color?:string}> = (selectedCard as any).vsmMixItems ?? [];
            const diasMes: number = (selectedCard as any).vsmDiasMes ?? 22;
            const totalMes = mixItems.reduce((s, i) => s + (i.qty || 0), 0);
            const totalDia = diasMes > 0 ? +(totalMes / diasMes).toFixed(1) : 0;
            const seqSlots = 20;
            const seq: Array<{label:string;color:string}> = [];
            if (totalMes > 0) {
              // DDA (Bresenham) clássico para Heijunka — interleave proporcional correto
              const acc = mixItems.map(() => 0);
              const rate = mixItems.map(item => (item.qty || 0) / totalMes);
              for (let s = 0; s < seqSlots; s++) {
                mixItems.forEach((_, i) => { acc[i] += rate[i]; });
                let bestIdx = 0;
                acc.forEach((v, i) => { if (v > acc[bestIdx]) bestIdx = i; });
                acc[bestIdx] -= 1;
                const item = mixItems[bestIdx];
                seq.push({ label: item.label || '?', color: item.color || PALETTE[bestIdx % PALETTE.length] });
              }
            }
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelClass}>Dias úteis / mês</label>
                    <input type="number" min="1" max="31" step="1"
                      value={diasMes}
                      onChange={(e) => onUpdateCardData(selectedCard.id, { vsmDiasMes: parseInt(e.target.value) || 22 } as any)}
                      className={inputClass + " text-center"}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Demanda total / dia</label>
                    <div className={"h-8 flex items-center px-2 rounded border border-slate-200 dark:border-slate-600 text-xs font-bold " + (totalDia > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400')}>
                      {totalDia > 0 ? `${totalDia.toLocaleString('pt-BR')} pcs` : '—'}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={labelClass + " mb-0"}>Mix de Produtos</label>
                    <button
                      type="button"
                      onClick={() => {
                        const color = PALETTE[mixItems.length % PALETTE.length];
                        onUpdateCardData(selectedCard.id, { vsmMixItems: [...mixItems, { label: '', qty: 0, color }] } as any);
                      }}
                      className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <Plus size={10} /> Produto
                    </button>
                  </div>
                  {mixItems.length === 0 && (
                    <p className="text-[10px] text-slate-400 italic py-1">Nenhum produto. Clique em + Produto para adicionar.</p>
                  )}
                  <div className="space-y-1.5">
                    {mixItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <input type="color"
                          value={item.color || PALETTE[idx % PALETTE.length]}
                          onChange={(e) => {
                            const updated = [...mixItems];
                            updated[idx] = { ...updated[idx], color: e.target.value };
                            onUpdateCardData(selectedCard.id, { vsmMixItems: updated } as any);
                          }}
                          className="w-6 h-6 rounded cursor-pointer flex-shrink-0"
                          style={{ padding: 1, border: 'none' }}
                        />
                        <input type="text"
                          value={item.label}
                          onChange={(e) => {
                            const updated = [...mixItems];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            onUpdateCardData(selectedCard.id, { vsmMixItems: updated } as any);
                          }}
                          className={inputClass + " flex-1 min-w-0"}
                          placeholder="Ex: LE, LD…"
                        />
                        <input type="number" min="0" step="1"
                          value={item.qty || ''}
                          onChange={(e) => {
                            const updated = [...mixItems];
                            updated[idx] = { ...updated[idx], qty: parseInt(e.target.value) || 0 };
                            onUpdateCardData(selectedCard.id, { vsmMixItems: updated } as any);
                          }}
                          className={inputClass + " w-20 text-right"}
                          placeholder="pcs/mês"
                        />
                        <button
                          type="button"
                          onClick={() => onUpdateCardData(selectedCard.id, { vsmMixItems: mixItems.filter((_, i) => i !== idx) } as any)}
                          className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                {totalMes > 0 && (
                  <>
                    <div className="flex items-center justify-between text-[10px] border-t border-slate-200 dark:border-slate-600 pt-2">
                      <span className="text-slate-500 dark:text-slate-400">Total mensal</span>
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{totalMes.toLocaleString('pt-BR')} pcs</span>
                    </div>
                    <div>
                      <label className={labelClass}>Sequência Heijunka</label>
                      <div className="flex gap-0.5 flex-wrap">
                        {seq.map((s, i) => (
                          <div key={i} title={s.label}
                            className="rounded-sm flex items-center justify-center text-white font-bold"
                            style={{ width: 18, height: 18, fontSize: 8, backgroundColor: s.color }}
                          >
                            {s.label.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {/* V200 — Pitch no ponto de controle OXOX */}
                {(() => {
                  const clientCard = allSheetCards.find((c: any) =>
                    c.tipo === 'card_final' || String(c.extType ?? '').toUpperCase() === 'CLIENTE' || c.vsmIcon === 'cliente'
                  ) as any | undefined;
                  let autoTaktSec: number | null = null;
                  if (clientCard) {
                    const rawDemand  = Number(clientCard.vsmDemanda ?? clientCard.entrada ?? 0);
                    const demandUnit = String(clientCard.unidadeDemanda ?? clientCard.vsmDemandaUnidade ?? '/dia').toLowerCase();
                    const workDays   = Math.max(0, Number(clientCard.vsmDiasMes ?? factoryWorkDays) || 0);
                    let dailyDemand  = 0;
                    if (rawDemand > 0) {
                      if (demandUnit.includes('mês') || demandUnit.includes('mes') || demandUnit.includes('/ms')) {
                        dailyDemand = workDays > 0 ? rawDemand / workDays : 0;
                      } else if (demandUnit.includes('hora')) {
                        dailyDemand = rawDemand * 8;
                      } else {
                        dailyDemand = rawDemand;
                      }
                    } else {
                      const mix = Array.isArray(clientCard.vsmDemandMix) ? clientCard.vsmDemandMix : [];
                      const totalMonthly = mix.reduce((s: number, item: any) => s + (Number(item?.qty) || 0), 0);
                      dailyDemand = totalMonthly > 0 && workDays > 0 ? totalMonthly / workDays : 0;
                    }
                    const availSec = Math.max(0, Number(factoryShifts) || 0) * Math.max(0, Number(factoryMinPerShift) || 0) * 60;
                    if (dailyDemand > 0 && availSec > 0) autoTaktSec = availSec / dailyDemand;
                  }
                  const nivelaPackSize = (selectedCard as any).vsmNivelamentoPackSize as number | undefined;
                  const pitch = autoTaktSec != null && nivelaPackSize != null && nivelaPackSize > 0
                    ? (autoTaktSec * nivelaPackSize) / 60
                    : null;
                  return (
                    <div className="space-y-1.5 rounded border border-emerald-200 bg-emerald-50/60 p-2 dark:border-emerald-500/20 dark:bg-emerald-900/20">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Pitch do Nivelamento</span>
                      <div>
                        <label className={labelClass}>Pack Size do Loop de Ritmo (un)</label>
                        <input
                          type="number" min="1" step="1"
                          value={nivelaPackSize ?? ''}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            onUpdateCardData(selectedCard.id, { vsmNivelamentoPackSize: v > 0 ? v : undefined } as any);
                          }}
                          className={inputClass + ' text-right'}
                          placeholder="20"
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      {autoTaktSec != null && (
                        <div className="flex items-center gap-1.5 rounded border border-emerald-100 bg-emerald-50 px-2 py-1 dark:border-emerald-500/20 dark:bg-emerald-900/30 opacity-80">
                          <span className="text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400">Takt</span>
                          <span className="font-mono text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">{(autoTaktSec / 60).toFixed(1)} min</span>
                        </div>
                      )}
                      {pitch != null ? (
                        <div className="flex items-center justify-between rounded border border-emerald-400 bg-emerald-100 px-2 py-1.5 dark:border-emerald-400/40 dark:bg-emerald-800/30">
                          <span className="text-[10px] font-bold uppercase text-emerald-800 dark:text-emerald-200">Pitch</span>
                          <span className="font-mono text-[14px] font-bold tracking-tight text-emerald-700 dark:text-emerald-300">{pitch.toFixed(1)} min</span>
                        </div>
                      ) : (
                        <p className="text-[9px] text-slate-400 italic">Defina o Pack Size e configure a demanda do cliente para calcular o Pitch</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          {selectedCard.vsmIcon === 'processo' && (
            <div className="space-y-2">
              {/* Campos Processo padrão */}
              {(selectedCard as any).vsmSubtype !== 'expedition' && (
                <>
                  {/* V229: Vitais sempre no topo — C/T, C/O, Disp, Operadores.
                      'Perfil do Processo' movido para accordion 'Configurações Avançadas'
                      abaixo dos vitais (Progressive Disclosure). */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelClass}>C/T (Cycle Time)</label>
                      <div className="relative h-8">
                        <input type="number" min="0" step="0.1"
                          value={(selectedCard as any).vsmCT ?? ''}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmCT: parseFloat(e.target.value) || 0 } as any)}
                          disabled={lockRootVsmParameters}
                          className="w-full h-full pl-3 pr-[52px] rounded-sm border border-slate-300 dark:border-slate-600 text-right text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"
                          placeholder="0"
                          onFocus={(e) => e.target.select()}
                        />
                        <select
                          value={(selectedCard as any).vsmCTUnit ?? 's'}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmCTUnit: e.target.value } as any)}
                          disabled={lockRootVsmParameters}
                          className="absolute right-0 top-0 h-full w-[50px] bg-transparent border-none text-[10px] font-bold text-blue-700 dark:text-blue-400 outline-none cursor-pointer appearance-none text-center pr-1 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                          <option value="s">s</option>
                          <option value="min">min</option>
                          <option value="h">h</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>C/O (Setup)</label>
                      <div className="relative w-full">
                        <input type="number" min="0" step="1"
                          value={(selectedCard as any).vsmCO ?? ''}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmCO: parseFloat(e.target.value) || 0 } as any)}
                          disabled={lockRootVsmParameters}
                          className={inputClass + " pr-8 text-right disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"}
                          placeholder="0"
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="absolute right-2 top-2 text-[10px] text-slate-400">min</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelClass}>Disponibilidade</label>
                      <div className="relative w-full">
                        <input type="number" min="0" max="100" step="1"
                          value={(selectedCard as any).vsmDisp ?? ''}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmDisp: parseFloat(e.target.value) || 0 } as any)}
                          disabled={lockRootVsmParameters}
                          className={inputClass + " pr-6 text-right disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"}
                          placeholder="100"
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="absolute right-2 top-2 text-[10px] text-slate-400">%</span>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Operadores</label>
                      <input type="number" min="0" step="1"
                        value={(selectedCard as any).vsmOp ?? ''}
                        onChange={(e) => onUpdateCardData(selectedCard.id, { vsmOp: parseFloat(e.target.value) || 0 } as any)}
                        disabled={lockRootVsmParameters}
                        className={inputClass + " text-right disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"}
                        placeholder="1"
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                  </div>
                  {lockRootVsmParameters && (
                    <div className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200">
                      Modo Otimização: parâmetros raiz bloqueados. Vincule um Kaizen de Redução para simular alteração.
                    </div>
                  )}
                  {/* V229: Configurações Avançadas (Progressive Disclosure).
                      Campos não-vitais ficam recolhidos por padrão. <details>
                      nativo evita dependência nova e é totalmente acessível. */}
                  <details className="group rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
                    <summary className="flex items-center justify-between cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors">
                      <span>Configurações Avançadas</span>
                      <svg
                        className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </summary>
                    <div className="px-3 pb-3 pt-1 space-y-2">
                      <div>
                        <label className={labelClass}>Perfil do Processo</label>
                        <select
                          value={(selectedCard as any).vsmPerfil ?? 'Dedicado'}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmPerfil: e.target.value } as any)}
                          className="w-full h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                        >
                          <option value="Dedicado">Dedicado</option>
                          <option value="Compartilhado">Compartilhado</option>
                        </select>
                      </div>
                    </div>
                  </details>
                  {/* V228: Kaizen Projection é dado de simulação/futuro.
                      Em 'current_clean' polui a leitura analítica do estado real;
                      renderizar apenas nos modos de oportunidade/futuro. */}
                  {/* V230: Kaizen Projection só existe em 'kaizen_work' (transição).
                      No estado futuro a projeção já virou realidade do card. */}
                  {viewMode === 'kaizen_work' && (
                  <div className="mt-4 border-t border-slate-200 dark:border-slate-700/60 pt-3">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        {selectedKaizenIsFlowCellScoped ? 'Kaizen da Célula' : 'Projeção de Melhoria (Kaizen)'}
                      </span>
                      {(selectedCard as any).vsmKaizenProjection && (
                        <button
                          type="button"
                          onClick={removeKaizenProjection}
                          className="inline-flex items-center gap-1 rounded-sm border border-red-300/70 px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-950/30"
                        >
                          <Trash2 size={10} /> Remover
                        </button>
                      )}
                    </div>
                    <div className="mb-2">
                      <label className={labelClass}>Título Kaizen</label>
                      <BufferedTextInput
                        value={(selectedCard as any).vsmKaizenProjection?.title ?? ''}
                        onCommit={updateKaizenProjectionTitle}
                        className={inputClass}
                        placeholder="Ex: SMED, Setup 0, Qualidade"
                      />
                    </div>
                    <div className="mb-2">
                      <label className={labelClass}>Tipo Kaizen</label>
                      <select
                        value={(selectedCard as any).vsmKaizenProjection?.improvementType ?? 'setup'}
                        onChange={(e) => updateKaizenProjectionType(e.target.value)}
                        className="w-full h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 text-sm font-semibold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      >
                        <option value="cycleTime">Redução de T/C</option>
                        <option value="setup">Setup/SMED</option>
                        <option value="quality">Qualidade</option>
                        <option value="tpm">TPM / Disponibilidade</option>
                        <option value="layout">Layout / Célula</option>
                      </select>
                    </div>
                    {selectedFlowCellId && (
                      <div className="mb-2 rounded-sm border border-cyan-300/60 bg-cyan-50/70 p-2 dark:border-cyan-500/30 dark:bg-cyan-950/20">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-800 dark:text-cyan-200">Projeção da Célula</span>
                          <span className="truncate text-[9px] font-semibold uppercase tracking-wide text-cyan-600/80 dark:text-cyan-300/80">{selectedFlowCellCards.length} processos</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={labelClass}>T/C Total Atual</label>
                            <input
                              readOnly
                              value={`${Math.round(selectedFlowCellCurrentSeconds)} s`}
                              className={inputClass + " text-right opacity-75"}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>T/C Total Projetado</label>
                            <div className="relative w-full">
                              <BufferedInput
                                value={selectedFlowCellProjectedSeconds || Math.round(selectedFlowCellCurrentSeconds)}
                                onCommit={updateSelectedFlowCellProjection}
                                className={inputClass + " pr-6 text-right"}
                                min={0}
                                step="1"
                              />
                              <span className="absolute right-2 top-2 text-[10px] text-slate-400">s</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {!selectedKaizenIsFlowCellScoped && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelClass}>Setup Projetado</label>
                        <div className="relative w-full">
                          <BufferedInput
                            value={getProjectedMetricValue('setup', Number((selectedCard as any).vsmCO ?? 0))}
                            onCommit={(val) => updateKaizenMetricProjection('setup', Number((selectedCard as any).vsmCO ?? 0), val, 'min')}
                            className={inputClass + " pr-8 text-right"}
                            min={0}
                            step="0.1"
                            commitOnChange={false}
                          />
                          <span className="absolute right-2 top-2 text-[10px] text-slate-400">min</span>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>T/C Projetado</label>
                        <div className="relative w-full">
                          <BufferedInput
                            value={getProjectedMetricValue('cycleTime', Number((selectedCard as any).vsmCT ?? 0))}
                            onCommit={(val) => updateKaizenMetricProjection('cycleTime', Number((selectedCard as any).vsmCT ?? 0), val, (selectedCard as any).vsmCTUnit ?? 's')}
                            className={inputClass + " pr-9 text-right"}
                            min={0}
                            step="0.1"
                            commitOnChange={false}
                          />
                          <span className="absolute right-2 top-2 text-[10px] text-slate-400">{(selectedCard as any).vsmCTUnit ?? 's'}</span>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Disponibilidade</label>
                        <div className="relative w-full">
                          <BufferedInput
                            value={getProjectedMetricValue('availability', Number((selectedCard as any).vsmDisp ?? 100))}
                            onCommit={(val) => updateKaizenMetricProjection('availability', Number((selectedCard as any).vsmDisp ?? 100), val, '%')}
                            className={inputClass + " pr-6 text-right"}
                            min={0}
                            max={100}
                            step="1"
                            commitOnChange={false}
                          />
                          <span className="absolute right-2 top-2 text-[10px] text-slate-400">%</span>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Qualidade</label>
                        <div className="relative w-full">
                          <BufferedInput
                            value={getProjectedMetricValue('quality', Number((selectedCard as any).percentLivreErros ?? 100))}
                            onCommit={(val) => updateKaizenMetricProjection('quality', Number((selectedCard as any).percentLivreErros ?? 100), val, '%')}
                            className={inputClass + " pr-6 text-right"}
                            min={0}
                            max={100}
                            step="1"
                            commitOnChange={false}
                          />
                          <span className="absolute right-2 top-2 text-[10px] text-slate-400">%</span>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                  )}
                </>
              )}

              {/* Campos Expedição */}
              {(selectedCard as any).vsmSubtype === 'expedition' && (
                <div className="rounded-sm border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/30 px-3 py-2">
                  <p className="text-[11px] text-orange-600 dark:text-orange-400 leading-snug">
                    Frequência e Lote de Entrega são configurados na <strong>aresta de transporte</strong> — conecte este nó ao Cliente para criá-la.
                  </p>
                </div>
              )}
            </div>
          )}
          {/* DADOS DE INVENTÁRIO */}
          {(selectedCard.tipo === 'inventory' || ['estoque','pulmao','supermercado','fifo'].includes(selectedCard.vsmIcon || '')) && (
            (() => {
              const variants: { label: string; qty: number }[] = (selectedCard as any).inventoryVariants ?? [];
              const explicitMode = (selectedCard as any).inventoryMode as 'coverage' | 'quantity' | undefined;
              const hasManualDays = (selectedCard as any).vsmDias !== undefined && (selectedCard as any).vsmDias !== null;
              const inventoryMode = explicitMode ?? (hasManualDays && variants.length === 0 ? 'coverage' : 'quantity');
              const isCoverageMode = inventoryMode === 'coverage';

              return (
                <div className="space-y-2">
                  {selectedCard.vsmIcon === 'estoque' && (selectedCard as any).vsmConvertedToIcon !== 'supermercado' && (selectedCard as any).vsmConvertedToIcon !== 'pulmao' && (
                    <div className="rounded-sm border border-orange-200 bg-orange-50 p-2 dark:border-orange-500/30 dark:bg-orange-950/30">
                      <button
                        type="button"
                        onClick={() => onUpdateCardData(selectedCard.id, {
                          vsmIcon: 'estoque',
                          atividade: selectedCard.atividade ?? 'Estoque',
                          inventoryMode: 'coverage',
                          vsmBufferDays: Number((selectedCard as any).vsmBufferDays ?? (selectedCard as any).vsmDias ?? 1) || 1,
                          vsmConvertedFromIcon: 'estoque',
                          vsmConvertedToIcon: 'supermercado',
                          vsmOriginalInventory: Number((selectedCard as any).inventory) || 0,
                          vsmOriginalInventoryUnit: (selectedCard as any).vsmUnit || 'LE',
                        } as any)}
                        className="flex h-8 w-full items-center justify-center rounded-sm bg-orange-500 px-2 text-[10px] font-extrabold uppercase tracking-[0.08em] text-white shadow-sm transition-colors hover:bg-orange-600"
                      >
                        Converter em Supermercado
                      </button>
                      <p className="mt-1 text-[10px] font-medium leading-snug text-orange-700 dark:text-orange-300">
                        Mantém o estoque original como comparação visual e ativa o buffer puxado na timeline.
                      </p>
                    </div>
                  )}

                  {selectedCard.vsmIcon === 'estoque' && (selectedCard as any).vsmConvertedToIcon !== 'supermercado' && (selectedCard as any).vsmConvertedToIcon !== 'pulmao' && (
                    <div className="rounded-sm border border-purple-200 bg-purple-50 p-2 dark:border-purple-500/30 dark:bg-purple-950/30">
                      <button
                        type="button"
                        onClick={() => onUpdateCardData(selectedCard.id, {
                          vsmIcon: 'estoque',
                          atividade: selectedCard.atividade ?? 'Estoque',
                          inventoryMode: 'coverage',
                          vsmBufferDays: Number((selectedCard as any).vsmBufferDays ?? (selectedCard as any).vsmDias ?? 1) || 1,
                          vsmConvertedFromIcon: 'estoque',
                          vsmConvertedToIcon: 'pulmao',
                          vsmOriginalInventory: Number((selectedCard as any).inventory) || 0,
                          vsmOriginalInventoryUnit: (selectedCard as any).vsmUnit || 'LE',
                        } as any)}
                        className="flex h-8 w-full items-center justify-center rounded-sm bg-purple-600 px-2 text-[10px] font-extrabold uppercase tracking-[0.08em] text-white shadow-sm transition-colors hover:bg-purple-700"
                      >
                        Converter em Pulmão Kanban
                      </button>
                      <p className="mt-1 text-[10px] font-medium leading-snug text-purple-700 dark:text-purple-300">
                        Cria pulmão kanban com sinal de retirada. Mantém estoque original como referência.
                      </p>
                    </div>
                  )}

                  {(['supermercado', 'pulmao'].includes(selectedCard.vsmIcon || '') || (selectedCard as any).vsmConvertedToIcon === 'supermercado' || (selectedCard as any).vsmConvertedToIcon === 'pulmao') && (
                    <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 dark:border-emerald-500/30 dark:bg-emerald-950/30">
                      <label className={labelClass}>Lead Time Fixo</label>
                      <div className="relative w-full">
                        <input type="number" min="0" step="0.1"
                          value={(selectedCard as any).vsmFixedLeadTimeDays ?? (selectedCard as any).vsmBufferDays ?? (selectedCard as any).vsmDias ?? 1}
                          onChange={(e) => {
                            const fixedLeadTimeDays = parseFloat(e.target.value) || 0;
                            onUpdateCardData(selectedCard.id, {
                              vsmFixedLeadTimeDays: fixedLeadTimeDays,
                              vsmBufferDays: fixedLeadTimeDays,
                              inventoryMode: 'coverage',
                            } as any);
                          }}
                          className={inputClass + " pr-10 text-right"}
                          placeholder="1.5"
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="absolute right-2 top-2 text-[10px] text-slate-400">dias</span>
                      </div>
                      <p className="mt-1 text-[10px] font-medium leading-snug text-emerald-700 dark:text-emerald-300">
                        Sistema puxado: a timeline ignora o estoque real e exibe este LT fixo.
                      </p>
                    </div>
                  )}

                  {(['supermercado'].includes(selectedCard.vsmIcon || '') || (selectedCard as any).vsmConvertedToIcon === 'supermercado' || (selectedCard as any).vsmConvertedToIcon === 'pulmao') && (() => {
                    // Processos individuais (sem flow cell)
                    const processoCards = allSheetCards.filter(c => (c as any).vsmIcon === 'processo' && c.id !== selectedCard.id && !(c as any).vsmFlowCellId);
                    // Células de fluxo contínuo: agrupar por vsmFlowCellId, usar o primeiro card como representante
                    const flowCellMap = new Map<string, { id: string; label: string; posicao: { x: number; y: number } }>();
                    allSheetCards.forEach(c => {
                      const cellId = (c as any).vsmFlowCellId as string | undefined;
                      if (!cellId || c.id === selectedCard.id) return;
                      if (!flowCellMap.has(cellId)) {
                        const label = (c as any).atividade || (c as any).vsmNome || `Célula ${cellId.slice(-4)}`;
                        flowCellMap.set(cellId, { id: cellId, label, posicao: c.posicao });
                      } else {
                        // Use label that looks like a group name if available
                        const existing = flowCellMap.get(cellId)!;
                        const names = [existing.label, (c as any).atividade].filter(Boolean);
                        if (names.length > 1 && !existing.label.includes('+')) {
                          flowCellMap.set(cellId, { ...existing, label: names.join(' + ') });
                        }
                      }
                    });
                    const flowCells = Array.from(flowCellMap.values());
                    const pullerId = (selectedCard as any).vsmKanbanPullerId as string | undefined;
                    const supplierId = (selectedCard as any).vsmKanbanSupplierId as string | undefined;
                    const postoId = (selectedCard as any).vsmKanbanPostoId as string | undefined;
                    const pcpmId  = (selectedCard as any).vsmKanbanPcpmId  as string | undefined;
                    const postoCards = allSheetCards.filter(c => (c as any).vsmIcon === 'kanban_posto');
                    const pcpmCards  = allSheetCards.filter(c => ['pcpm', 'nivelamento', 'info_eletro'].includes((c as any).vsmIcon));
                    const hasLoop = !!(pullerId || supplierId || postoId || pcpmId);
                    // Resolve display label for a stored id (could be cardId or cellId)
                    const resolveLabel = (id: string | undefined) => {
                      if (!id) return '';
                      const card = allSheetCards.find(c => c.id === id);
                      if (card) return card.atividade || id;
                      const cell = flowCellMap.get(id);
                      if (cell) return cell.label;
                      return id;
                    };
                    return (
                      <div className="rounded-sm border border-cyan-300 bg-cyan-50 p-2 dark:border-cyan-500/30 dark:bg-cyan-950/30 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Loop Kanban</span>
                          {hasLoop && (
                            <button
                              type="button"
                              onClick={() => onUpdateCardData(selectedCard.id, { vsmKanbanPullerId: undefined, vsmKanbanSupplierId: undefined, vsmKanbanPostoId: undefined, vsmKanbanPcpmId: undefined } as any)}
                              className="text-[9px] font-bold uppercase tracking-wide text-red-500 hover:text-red-700"
                            >
                              ✕ Remover
                            </button>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>Processo Puxador (retira)</label>
                          <select
                            value={pullerId ?? ''}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { vsmKanbanPullerId: e.target.value || undefined } as any)}
                            className={inputClass}
                          >
                            <option value="">— nenhum —</option>
                            {flowCells.length > 0 && <optgroup label="Células de Fluxo Contínuo">
                              {flowCells.map(cell => (
                                <option key={cell.id} value={cell.id}>{cell.label}</option>
                              ))}
                            </optgroup>}
                            {processoCards.length > 0 && <optgroup label="Processos">
                              {processoCards.map(c => (
                                <option key={c.id} value={c.id}>{c.atividade || c.id}</option>
                              ))}
                            </optgroup>}
                          </select>
                          <p className="mt-0.5 text-[9px] text-cyan-600 dark:text-cyan-400">Seta tracejada do topo do processo → {(selectedCard as any).vsmConvertedToIcon === 'pulmao' ? 'pulmão kanban' : 'supermercado'} (kanban retirada)</p>
                        </div>
                        <div>
                          <label className={labelClass}>Processo Fornecedor (produz)</label>
                          <select
                            value={supplierId ?? ''}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { vsmKanbanSupplierId: e.target.value || undefined } as any)}
                            className={inputClass}
                          >
                            <option value="">— nenhum —</option>
                            {flowCells.length > 0 && <optgroup label="Células de Fluxo Contínuo">
                              {flowCells.map(cell => (
                                <option key={cell.id} value={cell.id}>{cell.label}</option>
                              ))}
                            </optgroup>}
                            {processoCards.length > 0 && <optgroup label="Processos">
                              {processoCards.map(c => (
                                <option key={c.id} value={c.id}>{c.atividade || c.id}</option>
                              ))}
                            </optgroup>}
                          </select>
                          <p className="mt-0.5 text-[9px] text-cyan-600 dark:text-cyan-400">Seta tracejada do {(selectedCard as any).vsmConvertedToIcon === 'pulmao' ? 'pulmão kanban' : 'supermercado'} → topo do processo (kanban produção)</p>
                        </div>
                        {/* V230: Configurações avançadas — sub-loops e packs raramente alterados */}
                        <details className="group rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-slate-50/60 dark:bg-slate-800/30">
                          <summary className="flex items-center justify-between cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors">
                            <span>Configurações Avançadas</span>
                            <svg
                              className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180"
                              viewBox="0 0 24 24" fill="none" stroke="currentColor"
                              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                              aria-hidden
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </summary>
                          <div className="px-3 pb-3 pt-1 space-y-2">
                        <div>
                          <label className={labelClass}>Posto Kanban (intermediário)</label>
                          <select
                            value={postoId ?? ''}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { vsmKanbanPostoId: e.target.value || undefined } as any)}
                            className={inputClass}
                          >
                            <option value="">— nenhum —</option>
                            {postoCards.map(c => (
                              <option key={c.id} value={c.id}>{c.atividade || 'Posto Kanban'}</option>
                            ))}
                          </select>
                          <p className="mt-0.5 text-[9px] text-cyan-600 dark:text-cyan-400">Ícone posto kanban entre supermercado e fornecedor (duas setas de produção)</p>
                        </div>
                        <div>
                          <label className={labelClass}>PCPM / MRP (destino do posto)</label>
                          <select
                            value={pcpmId ?? ''}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { vsmKanbanPcpmId: e.target.value || undefined } as any)}
                            className={inputClass}
                          >
                            <option value="">— nenhum —</option>
                            {pcpmCards.map(c => (
                              <option key={c.id} value={c.id}>{c.atividade || 'PCPM'}</option>
                            ))}
                          </select>
                          <p className="mt-0.5 text-[9px] text-cyan-600 dark:text-cyan-400">Seta de kanban produção saindo do posto kanban até o PCPM/MRP</p>
                        </div>
                        {/* V200 — Pack Size por Aresta (Maturidade de Fluxo) */}
                        <div className="space-y-2 rounded border border-cyan-200 bg-cyan-100/60 p-2 dark:border-cyan-500/20 dark:bg-cyan-900/20">
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Unidade de Carga (Pack)</span>
                          {(() => {
                            const pullerCard   = allSheetCards.find((c: any) => c.id === (selectedCard as any).vsmKanbanPullerId) as any | undefined;
                            const supplierCard = allSheetCards.find((c: any) => c.id === (selectedCard as any).vsmKanbanSupplierId) as any | undefined;
                            const isPullerCell     = pullerCard   && (pullerCard.vsmIcon   === 'processo');
                            const isSupplierExternal = supplierCard && ['fornecedor', 'externo'].includes(supplierCard.vsmIcon ?? '');
                            const retPackSize  = (selectedCard as any).vsmKanbanRetPackSize  as number | undefined;
                            const prodPackSize = (selectedCard as any).vsmKanbanProdPackSize as number | undefined;
                            return (
                              <>
                                {/* Seta de Retirada */}
                                {(selectedCard as any).vsmKanbanPullerId && (
                                  <div>
                                    <label className={labelClass}>
                                      Retirada (Puxador → SM)
                                      {isPullerCell && <span className="ml-1 text-emerald-600 dark:text-emerald-400">• Loop de Ritmo</span>}
                                    </label>
                                    <input
                                      type="number" min="1" step="1"
                                      value={retPackSize ?? ''}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value);
                                        onUpdateCardData(selectedCard.id, { vsmKanbanRetPackSize: v > 0 ? v : undefined } as any);
                                      }}
                                      className={inputClass + ' text-right'}
                                      placeholder={isPullerCell ? '20' : '—'}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    {isPullerCell && retPackSize == null && (
                                      <p className="mt-0.5 text-[9px] text-emerald-600 dark:text-emerald-400">Sugestão: 20 (ritmo da célula)</p>
                                    )}
                                  </div>
                                )}
                                {/* Seta de Produção */}
                                {((selectedCard as any).vsmKanbanSupplierId || (selectedCard as any).vsmKanbanPostoId) && (
                                  <div>
                                    <label className={labelClass}>
                                      Produção (SM → Fornecedor)
                                      {isSupplierExternal && <span className="ml-1 text-amber-600 dark:text-amber-400">• Loop de Abastecimento</span>}
                                    </label>
                                    <input
                                      type="number" min="1" step="1"
                                      value={prodPackSize ?? ''}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value);
                                        onUpdateCardData(selectedCard.id, { vsmKanbanProdPackSize: v > 0 ? v : undefined } as any);
                                      }}
                                      className={inputClass + ' text-right'}
                                      placeholder={isSupplierExternal ? '500' : '20'}
                                      onFocus={(e) => e.target.select()}
                                    />
                                    {isSupplierExternal && (
                                      <p className="mt-0.5 text-[9px] text-amber-600 dark:text-amber-400">Loop externo — não influencia Pitch da célula</p>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                          </div>
                        </details>
                      </div>
                    );
                  })()}

                  {selectedCard.vsmIcon === 'fifo' && (
                    <div className="grid grid-cols-2 gap-2 rounded-sm border border-purple-200 bg-purple-50 p-2 dark:border-purple-500/30 dark:bg-purple-950/30">
                      <div>
                        <label className={labelClass}>Capacidade FIFO</label>
                        <input type="number" min="1" step="1"
                          value={(selectedCard as any).vsmFifoMaxCapacity ?? 50}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmFifoMaxCapacity: parseFloat(e.target.value) || 50 } as any)}
                          className={inputClass + " text-right"}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>WIP Atual</label>
                        <input type="number" min="0" step="1"
                          value={(selectedCard as any).vsmFifoCurrentWip ?? 0}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmFifoCurrentWip: parseFloat(e.target.value) || 0 } as any)}
                          className={inputClass + " text-right"}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>Tipo de informação</label>
                    <div className="inline-grid grid-cols-2 gap-1 rounded-md bg-slate-100 dark:bg-slate-800 p-1 w-full">
                      <button
                        type="button"
                        onClick={() => onUpdateCardData(selectedCard.id, { inventoryMode: 'coverage' } as any)}
                        className={`h-7 rounded text-xs font-medium transition-colors ${isCoverageMode ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                      >
                        Cobertura
                      </button>
                      <button
                        type="button"
                        onClick={() => onUpdateCardData(selectedCard.id, { inventoryMode: 'quantity' } as any)}
                        className={`h-7 rounded text-xs font-medium transition-colors ${!isCoverageMode ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                      >
                        Quantidade
                      </button>
                    </div>
                  </div>

                  {isCoverageMode ? (
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
                      <div>
                        <label className={labelClass}>Dias de Cobertura</label>
                        <input type="number" min="0" step="0.5"
                          value={(selectedCard as any).vsmDias ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                            onUpdateCardData(selectedCard.id, { vsmDias: v } as any);
                          }}
                          className={inputClass + " text-right"}
                          placeholder="0"
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Material</label>
                        <select
                          value={(selectedCard as any).vsmUnit || 'bobina'}
                          onChange={(e) => onUpdateCardData(selectedCard.id, { vsmUnit: e.target.value } as any)}
                          className={inputClass}
                        >
                          <option value="bobina">Bobinas</option>
                          <option value="pç">Peças</option>
                          <option value="kg">KG</option>
                          <option value="pallet">Pallets</option>
                          <option value="cx">Caixas</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={labelClass}>Quantidade</label>
                          <input type="number" min="0" step="1"
                            value={(selectedCard as any).inventory ?? ''}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { inventory: parseFloat(e.target.value) || 0 } as any)}
                            className={inputClass + " text-right"}
                            placeholder="0"
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Unidade</label>
                          <select
                            value={(selectedCard as any).vsmUnit || 'pç'}
                            onChange={(e) => onUpdateCardData(selectedCard.id, { vsmUnit: e.target.value } as any)}
                            className={inputClass}
                          >
                            <option value="pç">Peças</option>
                            <option value="kg">KG</option>
                            <option value="pallet">Pallets</option>
                            <option value="cx">Caixas</option>
                            <option value="bobina">Bobinas</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Variantes</label>
                        <div className="flex flex-col gap-1 mt-0.5">
                          {variants.map((v, i) => (
                            <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_24px] items-center gap-1">
                              <input type="text" placeholder="Ex: LE"
                                value={v.label}
                                onChange={(e) => {
                                  const next = variants.map((x, j) => j === i ? { ...x, label: e.target.value } : x);
                                  onUpdateCardData(selectedCard.id, { inventoryVariants: next } as any);
                                }}
                                className={inputClass + " !w-full min-w-0 text-center !px-1"}
                              />
                              <input type="number" min={0} placeholder="0"
                                value={v.qty || ''}
                                onChange={(e) => {
                                  const next = variants.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) || 0 } : x);
                                  onUpdateCardData(selectedCard.id, { inventoryVariants: next } as any);
                                }}
                                className={inputClass + " !w-full min-w-0 text-right"}
                              />
                              <button
                                onClick={() => onUpdateCardData(selectedCard.id, { inventoryVariants: variants.filter((_, j) => j !== i) } as any)}
                                className="h-[28px] w-[24px] flex items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-canvas)] hover:bg-red-500/10 hover:border-red-400/50 text-[10px] text-[var(--text-muted)] hover:text-red-400 transition-colors cursor-pointer shrink-0"
                              >x</button>
                            </div>
                          ))}
                          <button
                            onClick={() => onUpdateCardData(selectedCard.id, { inventoryVariants: [...variants, { label: '', qty: 0 }] } as any)}
                            className="w-full h-[26px] flex items-center justify-center gap-1 rounded border border-dashed border-[var(--border-default)] bg-transparent hover:bg-[var(--bg-elevated)] text-[9px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer select-none"
                          >
                            <span>+</span><span>Adicionar Variante</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {!isCoverageMode && (() => {
                    const qty = parseFloat((selectedCard as any).inventory) || 0;
                    const demand = parseFloat((selectedCard as any).vsmDemanda) || 0;
                    const dias = demand > 0 ? (qty / demand).toFixed(1) : null;
                    return dias !== null ? (
                      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-amber-50 border border-amber-200">
                        <span className="text-[10px] font-semibold text-amber-700">Dias de Estoque</span>
                        <span className="text-[11px] font-bold text-amber-800">{dias} dias</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              );
            })()
          )}
          {/* TRANSPORTE — Frequência */}
          {/* V201 Estado Futuro: ocultar card no mapa futuro — V230 tipografia suavizada */}
          {['processo', 'estoque', 'supermercado', 'pulmao', 'nivelamento'].includes(selectedCard.vsmIcon ?? '') && (
            <div className="flex items-center justify-between rounded-md border border-slate-200/70 bg-slate-50/60 px-3 py-2 dark:border-slate-700/50 dark:bg-slate-800/40">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Visibilidade no Estado Futuro</span>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Ocultar este card no Mapa Futuro</span>
              </div>
              <button
                type="button"
                onClick={() => onUpdateCardData(selectedCard.id, { vsmFuturoHide: !(selectedCard as any).vsmFuturoHide } as any)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${(selectedCard as any).vsmFuturoHide ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                role="switch"
                aria-checked={(selectedCard as any).vsmFuturoHide ?? false}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${(selectedCard as any).vsmFuturoHide ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          )}
          {/* V201 Estado Futuro: T/C Integrado da Célula (somente se card estiver em uma célula) */}
          {selectedCard.vsmIcon === 'processo' && selectedFlowCellId && (
            <div className="space-y-1.5 rounded border border-emerald-200 bg-emerald-50/60 p-2 dark:border-emerald-500/20 dark:bg-emerald-900/20">
              <span className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-300">Célula — Estado Futuro</span>
              <div>
                <label className={labelClass}>T/C Integrado da Célula (s)</label>
                <input
                  type="number" min="1" step="1"
                  value={selectedFlowCellProjectedSeconds || ''}
                  onChange={(e) => updateSelectedFlowCellProjection(Math.max(1, parseInt(e.target.value) || 1))}
                  className={inputClass + ' text-right'}
                  placeholder={String(Math.round(selectedFlowCellCurrentSeconds))}
                  onFocus={(e) => e.target.select()}
                />
                <p className="mt-0.5 text-[9px] text-emerald-600 dark:text-emerald-400">
                  Σ atual: {Math.round(selectedFlowCellCurrentSeconds)}s — exibido no frame da célula no mapa futuro
                </p>
              </div>
            </div>
          )}
          {/* TRANSPORTE — Frequência */}
          {selectedCard.vsmIcon === 'entrega' && (
            <div className="space-y-2">
              <div>
                <label className={labelClass}>Frequência de Entrega</label>
                <input type="text"
                  value={(selectedCard as any).vsmFrequencia ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmFrequencia: e.target.value } as any)}
                  className={inputClass}
                  placeholder="Ex: 2x/semana"
                />
              </div>
            </div>
          )}
          {/* CAIXA DE INFORMAÇÃO — Parâmetros Técnicos (V139.0) */}
          {selectedCard.vsmIcon === 'comunicacao' && (() => {
            const bound = (selectedCard as any).vsmBoundConnection as { sourceId: string; targetId: string; connectionId: string } | undefined;

            const handleTransmissionChange = (method: 'Eletrônico' | 'Manual') => {
              // V159.0: Single Source of Truth — atualiza SEMPRE o comunicacao card E a rawConn do card fonte
              onUpdateCardData(selectedCard.id, { vsmInfoTransmissionMethod: method } as any);
              const selfCard = cards.find(c => c.id === selectedCard.id);
              // Atualizar conexoes_saida próprias (se existirem)
              if (selfCard && (selfCard.conexoes_saida ?? []).length > 0) {
                const updatedConns = selfCard.conexoes_saida!.map(conn => ({ ...conn, vsmInfoTransmission: method }));
                onUpdateCardData(selfCard.id, { conexoes_saida: updatedConns });
              }
              // SEMPRE atualizar a rawConn no card fonte via vsmBoundConnection
              if (bound) {
                const sourceCard = cards.find(c => c.id === bound.sourceId);
                if (sourceCard) {
                  const updatedConns = (sourceCard.conexoes_saida ?? []).map(conn =>
                    // V159.2: match por connectionId OU targetId (targetId é estável)
                    (conn.id === bound.connectionId || conn.targetId === bound.targetId)
                      ? { ...conn, vsmInfoTransmission: method }
                      : conn,
                  );
                  onUpdateCardData(sourceCard.id, { conexoes_saida: updatedConns });
                }
              }
            };

            return (
              <div className="space-y-2">
                {/* Periodicidade */}
                <div>
                  <label className={labelClass}>Periodicidade</label>
                  <select
                    value={(selectedCard as any).vsmInfoPeriodicity ?? ''}
                    onChange={(e) => onUpdateCardData(selectedCard.id, { vsmInfoPeriodicity: e.target.value } as any)}
                    className="w-full h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  >
                    <option value="">— Selecione —</option>
                    <option value="Tempo Real">Tempo Real</option>
                    <option value="Horária">Horária</option>
                    <option value="Diária">Diária</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Quinzenal">Quinzenal</option>
                    <option value="Mensal">Mensal</option>
                    <option value="Sob Demanda">Sob Demanda</option>
                  </select>
                </div>
                {/* Método de Transmissão */}
                <div>
                  <label className={labelClass}>Método de Transmissão</label>
                  <div className="grid grid-cols-2 gap-1 rounded-sm border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-0.5">
                    {(['Eletrônico', 'Manual'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleTransmissionChange(m)}
                        className={`h-7 rounded-sm text-[10px] font-bold uppercase transition-colors cursor-pointer select-none ${
                          (selectedCard as any).vsmInfoTransmissionMethod === m
                            ? m === 'Eletrônico'
                              ? 'bg-cyan-500 text-white'
                              : 'bg-slate-600 text-white'
                            : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        {m === 'Eletrônico' ? '⚡ Eletrônico' : '✋ Manual'}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Info Lead Time */}
                <div>
                  <label className={labelClass}>Info Lead Time</label>
                  <div className="relative w-full">
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={(selectedCard as any).vsmInfoLeadTime ?? ''}
                      onChange={(e) => onUpdateCardData(selectedCard.id, { vsmInfoLeadTime: parseFloat(e.target.value) || 0 } as any)}
                      className={inputClass + ' pr-10 text-right'}
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                    />
                    <span className="absolute right-2 top-2 text-[10px] text-slate-400">dias</span>
                  </div>
                </div>
                {/* Batch Size */}
                <div>
                  <label className={labelClass}>Lote de Informação</label>
                  <div className="relative w-full">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={(selectedCard as any).vsmInfoBatchSize ?? ''}
                      onChange={(e) => onUpdateCardData(selectedCard.id, { vsmInfoBatchSize: parseFloat(e.target.value) || 0 } as any)}
                      className={inputClass + ' pr-10 text-right'}
                      placeholder="1"
                      onFocus={(e) => e.target.select()}
                    />
                    <span className="absolute right-2 top-2 text-[10px] text-slate-400">itens</span>
                  </div>
                </div>
                {/* Badge de diagnóstico rápido */}
                {(selectedCard as any).vsmInfoLeadTime > 0 && (
                  <div className="flex items-center justify-between rounded bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800 px-2 py-1.5">
                    <span className="text-[10px] font-semibold text-cyan-700 dark:text-cyan-300">Info LT impacta PLT</span>
                    <span className="text-[11px] font-bold text-cyan-800 dark:text-cyan-200">+{(selectedCard as any).vsmInfoLeadTime} dias</span>
                  </div>
                )}
              </div>
            );
          })()}
          {/* PLANEJAMENTO / DEMANDA — Externo (card_final / fornecedor sem config avançada) */}
          {/* Para card_inicial FACTORY a seção "VSM FACTORY — Externo" (acima) cobre tudo. */}
          {(selectedCard.vsmIcon && ['fornecedor','cliente','externo'].includes(selectedCard.vsmIcon) && selectedCard.tipo !== 'card_inicial') && (
            <div className="space-y-2">
              {/* Nome do fornecedor/cliente */}
              <div>
                <label className={labelClass}>Nome</label>
                <input type="text"
                  value={(selectedCard as any).vsmNome ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmNome: e.target.value } as any)}
                  className={inputClass}
                  placeholder="Ex: Aço Brasil"
                />
              </div>
              {/* Produto */}
              <div>
                <label className={labelClass}>Produto</label>
                <input type="text"
                  value={(selectedCard as any).vsmProduto ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmProduto: e.target.value } as any)}
                  className={inputClass}
                  placeholder="Ex: Chapa metálica"
                />
              </div>
              {/* Frequência de entrega */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Frequência de Entrega</label>
                  <input type="text"
                    value={(selectedCard as any).vsmFrequencia ?? ''}
                    onChange={(e) => onUpdateCardData(selectedCard.id, { vsmFrequencia: e.target.value } as any)}
                    className={inputClass}
                    placeholder="Ex: 2x/semana"
                  />
                </div>
                <div>
                  <label className={labelClass}>Demanda</label>
                  <div className="relative w-full">
                    <input type="number" min="0" step="1"
                      value={(selectedCard as any).vsmDemanda ?? ''}
                      onChange={(e) => onUpdateCardData(selectedCard.id, { vsmDemanda: parseFloat(e.target.value) || 0 } as any)}
                      className={inputClass + " pr-12 text-right"}
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                    />
                    <span className="absolute right-2 top-2 text-[10px] text-slate-400">pç/dia</span>
                  </div>
                </div>
              </div>
              {/* Mix de Produtos — repeater */}
              <label className={labelClass + " mb-1"}>Mix de Produtos (qtd/mês)</label>
              <div className="flex flex-col gap-1">
                {((selectedCard as any).vsmDemandMix ?? [] as Array<{label:string;qty:number}>).map(
                  (item: {label:string;qty:number}, i: number) => {
                    const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                    return (
                      <div key={i} className="flex items-center gap-1 min-w-0">
                        <input type="text" placeholder="Ex: LE"
                          value={item.label}
                          onChange={(e) => {
                            const next = mix.map((x,j) => j===i ? {...x,label:e.target.value} : x);
                            onUpdateCardData(selectedCard.id, { vsmDemandMix: next } as any);
                          }}
                          className={inputClass + " w-[52px] text-center !px-1"}
                        />
                        <input type="number" min={0} placeholder="0"
                          value={item.qty || ''}
                          onChange={(e) => {
                            const next = mix.map((x,j) => j===i ? {...x,qty:Number(e.target.value)||0} : x);
                            const total = next.reduce((s,x) => s+x.qty, 0);
                            const dias = parseFloat((selectedCard as any).vsmDiasMes) || 20;
                            const daily = dias > 0 ? total / dias : undefined;
                            onUpdateCardData(selectedCard.id, { vsmDemandMix: next, ...(daily !== undefined ? { vsmDemanda: daily } : {}) } as any);
                          }}
                          className={inputClass + " w-[80px] min-w-0 text-right"}
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="text-[10px] text-slate-400 shrink-0">pç/mês</span>
                        <button
                          onClick={() => {
                            const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                            onUpdateCardData(selectedCard.id, { vsmDemandMix: mix.filter((_,j) => j!==i) } as any);
                          }}
                          className="h-[28px] w-[24px] flex items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-canvas)] hover:bg-red-500/10 hover:border-red-400/50 text-[10px] text-[var(--text-muted)] hover:text-red-400 transition-colors cursor-pointer shrink-0"
                        >×</button>
                      </div>
                    );
                  }
                )}
                <button
                  onClick={() => {
                    const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                    onUpdateCardData(selectedCard.id, { vsmDemandMix: [...mix, {label:'',qty:0}] } as any);
                  }}
                  className="text-[9px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-0.5 rounded flex items-center gap-1 transition-colors border border-blue-100 hover:border-blue-300 self-start mt-0.5"
                >
                  <span>+</span><span>Adicionar Variante</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Turnos</label>
                  <select
                    value={(selectedCard as any).vsmTurnos ?? '1'}
                    onChange={(e) => onUpdateCardData(selectedCard.id, { vsmTurnos: e.target.value } as any)}
                    className={inputClass}
                  >
                    <option value="1">1 turno</option>
                    <option value="2">2 turnos</option>
                    <option value="3">3 turnos</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Horas Disponíveis / Turno</label>
                <div className="relative w-full">
                  <input type="number" min="1" max="12" step="0.5"
                    value={(selectedCard as any).vsmHorasTurno ?? '8'}
                    onChange={(e) => onUpdateCardData(selectedCard.id, { vsmHorasTurno: parseFloat(e.target.value) || 8 } as any)}
                    className={inputClass + " pr-6 text-right"}
                    placeholder="8"
                    onFocus={(e) => e.target.select()}
                  />
                  <span className="absolute right-2 top-2 text-[10px] text-slate-400">h</span>
                </div>
              </div>
              {/* Takt Time — calculado automaticamente */}
              {(() => {
                const demanda = parseFloat((selectedCard as any).vsmDemanda) || 0;
                const turnos = parseFloat((selectedCard as any).vsmTurnos) || 1;
                const horas = parseFloat((selectedCard as any).vsmHorasTurno) || 8;
                const availSec = turnos * horas * 3600;
                const tt = demanda > 0 ? availSec / demanda : null;
                // Verifica se algum card processo tem CT > TT (gargalo)
                const maxCTsec = allSheetCards
                  .filter((c: any) => c.vsmIcon === 'processo')
                  .reduce((max: number, c: any) => {
                    const raw = parseFloat(c.vsmCT) || 0;
                    const unit = (c as any).vsmCTUnit ?? 's';
                    const sec = unit === 'min' ? raw * 60 : unit === 'h' ? raw * 3600 : raw;
                    return Math.max(max, sec);
                  }, 0);
                const isBottleneck = tt !== null && maxCTsec > tt;
                return (
                  <div className={`flex items-center justify-between px-2 py-1.5 rounded border ${
                    isBottleneck ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'
                  }`}>
                    <span className={`text-[10px] font-semibold ${
                      isBottleneck ? 'text-red-700' : 'text-blue-700'
                    }`}>{isBottleneck ? '⚠ Takt Time' : 'Takt Time'}</span>
                    <span className={`text-[11px] font-bold ${
                      isBottleneck ? 'text-red-600' : 'text-blue-800'
                    }`}>{tt !== null ? `${tt.toFixed(1)} s/pç` : '—'}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════════
             VSM FACTORY — Externo (card_inicial com vsmIcon externo/fornecedor/cliente)
             FONTE ÚNICA para propriedades de entidade externa no canvas principal.
             NÃO editar VsmPropertiesSidebar.tsx para este fim — é outro canvas.
             Campos: vsmNome, vsmProduto, vsmFrequencia, vsmDemanda,
                     vsmShifts, vsmWorkMin, vsmDemandMix[], vsmPackUnit, vsmPackQty
             ══════════════════════════════════════════════════════════════════════════ */}
        {mapType === 'FACTORY' && selectedCard && selectedCard.tipo === 'card_inicial' && selectedCard.vsmIcon && ['fornecedor','cliente','externo'].includes(selectedCard.vsmIcon) && (
        <div className={sectionClass} style={{ order: -1 }}>
          {/* ── Dados da Entidade ─────────────────────────────────────── */}
          <label className={labelClass + " mb-3 flex items-center gap-1 text-emerald-700"}>
            <span style={{fontSize:10}}>◈</span>{' '}Dados da Entidade Externa
          </label>
          <div className="space-y-2">
            <div>
              <label className={labelClass}>Nome</label>
              <input type="text"
                value={(selectedCard as any).vsmNome ?? ''}
                onChange={(e) => onUpdateCardData(selectedCard.id, { vsmNome: e.target.value, nome_fluxo: e.target.value } as any)}
                className={inputClass}
                placeholder="Ex: Aço Brasil"
              />
            </div>
            <div>
              <label className={labelClass}>Produto</label>
              <input type="text"
                value={(selectedCard as any).vsmProduto ?? ''}
                onChange={(e) => onUpdateCardData(selectedCard.id, { vsmProduto: e.target.value } as any)}
                className={inputClass}
                placeholder="Ex: Chapa metálica"
              />
            </div>
            <div>
              <label className={labelClass}>Frequência Entrega</label>
              <input type="text"
                value={(selectedCard as any).vsmFrequencia ?? ''}
                onChange={(e) => onUpdateCardData(selectedCard.id, { vsmFrequencia: e.target.value } as any)}
                className={inputClass}
                placeholder="Ex: 2x/sem"
              />
            </div>
          </div>

          {/* ── Demanda do Cliente ────────────────────────────────────── */}
          <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
            <label className={labelClass + " mb-2 flex items-center gap-1 text-blue-700 dark:text-blue-400"}>
              <span style={{fontSize:10}}>⊕</span>{' '}Demanda do Cliente
            </label>

            {/* Takt badge V104.0 — usa config global da fábrica */}
            {(() => {
              const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
              const totalMonthly = mix.reduce((s, v) => s + (v.qty || 0), 0);
              const dailyDemand  = factoryWorkDays > 0 ? totalMonthly / factoryWorkDays : 0;
              const availableSecPerDay = factoryShifts * factoryMinPerShift * 60;
              const takt = dailyDemand > 0 ? availableSecPerDay / dailyDemand : 0;
              if (takt <= 0) return null;
              return (
                <div className="rounded border border-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-600/50 mb-3 overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="text-[9px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">Takt Time</div>
                      <div className="text-[15px] font-black text-cyan-700 dark:text-cyan-300" style={{fontFamily:'monospace'}}>
                        {takt >= 60 ? `${(takt/60).toFixed(1)} min` : `${takt.toFixed(1)} s`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">Demanda Diária</div>
                      <div className="text-[13px] font-bold text-cyan-700 dark:text-cyan-300" style={{fontFamily:'monospace'}}>
                        {Math.round(dailyDemand).toLocaleString('pt-BR')} pç/dia
                      </div>
                    </div>
                  </div>
                  <div className="px-3 pb-2 text-[9px] text-cyan-500 dark:text-cyan-400" style={{fontFamily:'monospace'}}>
                    {totalMonthly.toLocaleString('pt-BR')} pç/mês ÷ {factoryWorkDays} dias · {factoryShifts}T × {factoryMinPerShift}min
                  </div>
                </div>
              );
            })()}

            {/* Mix de Produtos — repeater (qty = mensal) */}
            <label className={labelClass + " mb-1"}>Mix de Produtos (qtd/mês)</label>
            <div className="space-y-1">
              {((selectedCard as any).vsmDemandMix ?? [] as Array<{label:string;qty:number}>).map(
                (variant: {label:string;qty:number}, i: number) => {
                  const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <input
                        type="text"
                        placeholder="Var."
                        value={variant.label}
                        onChange={(e) => {
                          const next = mix.map((v, j) => j === i ? {...v, label: e.target.value} : v);
                          onUpdateCardData(selectedCard.id, { vsmDemandMix: next } as any);
                        }}
                        className="w-[52px] h-8 text-center text-xs border border-slate-300 dark:border-slate-600 rounded-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <input
                        type="number" min={0} placeholder="0"
                        value={variant.qty || ''}
                        onChange={(e) => {
                          const next = mix.map((v, j) => j === i ? {...v, qty: parseFloat(e.target.value) || 0} : v);
                          const total = next.reduce((s, v) => s + (v.qty || 0), 0);
                          const daily = factoryWorkDays > 0 && total > 0 ? Math.round(total / factoryWorkDays) : undefined;
                          onUpdateCardData(selectedCard.id, { vsmDemandMix: next, ...(daily !== undefined ? { vsmDemanda: daily } : {}) } as any);
                        }}
                        className="flex-1 h-8 text-right text-xs border border-slate-300 dark:border-slate-600 rounded-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400 px-2"
                        onFocus={(e) => e.target.select()}
                      />
                      <span className="text-[9px] text-slate-400 shrink-0">pç/mês</span>
                      <button
                        onClick={() => {
                          const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                          onUpdateCardData(selectedCard.id, { vsmDemandMix: mix.filter((_,j) => j !== i) } as any);
                        }}
                        className="w-6 h-8 flex items-center justify-center rounded-sm border border-slate-300 dark:border-slate-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 text-slate-400 hover:text-red-500 text-xs transition-colors"
                      >×</button>
                    </div>
                  );
                }
              )}
              <button
                onClick={() => {
                  const mix: Array<{label:string;qty:number}> = (selectedCard as any).vsmDemandMix ?? [];
                  onUpdateCardData(selectedCard.id, { vsmDemandMix: [...mix, {label:'', qty:0}] } as any);
                }}
                className="w-full h-7 flex items-center justify-center gap-1 border border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-sm text-[10px] text-slate-400 hover:text-blue-500 transition-colors"
              >
                <span>+</span><span>Adicionar Variante</span>
              </button>
            </div>
          </div>

          {/* ── Configurações Logísticas (V232 Accordion) — hierarquia primária / secundária ── */}
          <details className="group mt-4 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
            <summary className="flex items-center justify-between cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
              <span className="flex items-center gap-1"><span style={{fontSize:10}} className="text-amber-600 dark:text-amber-400">▣</span> Configurações Logísticas</span>
              <svg
                className="w-3.5 h-3.5 text-slate-400 transition-transform group-open:rotate-180"
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <div className="px-3 pb-3 pt-1">

            {/* Unidade Primária */}
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Unidade Primária</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className={labelClass}>Nome</label>
                <input type="text" placeholder="ex: Bandeja"
                  value={(selectedCard as any).vsmPackPrimaryName ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmPackPrimaryName: e.target.value } as any)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Peças / unid.</label>
                <input type="number" min={1} placeholder="20"
                  value={(selectedCard as any).vsmPackPrimaryQty ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmPackPrimaryQty: parseInt(e.target.value) || 0 } as any)}
                  className={inputClass + " text-right"}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>

            {/* Unidade Secundária */}
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Unidade Secundária</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className={labelClass}>Nome</label>
                <input type="text" placeholder="ex: Pallet"
                  value={(selectedCard as any).vsmPackSecondaryName ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmPackSecondaryName: e.target.value } as any)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Unid. prim. / sec.</label>
                <input type="number" min={1} placeholder="10"
                  value={(selectedCard as any).vsmPackSecondaryQty ?? ''}
                  onChange={(e) => onUpdateCardData(selectedCard.id, { vsmPackSecondaryQty: parseInt(e.target.value) || 0 } as any)}
                  className={inputClass + " text-right"}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>

            {/* Lote de entrega + Poka-Yoke */}
            {(() => {
              const lot  = Number((selectedCard as any).vsmDeliveryLot || 0);
              const prim = Number((selectedCard as any).vsmPackPrimaryQty || 0);
              const isInvalid = lot > 0 && prim > 0 && lot % prim !== 0;
              const secQty = Number((selectedCard as any).vsmPackSecondaryQty || 0);
              const suggestedLot = prim > 0 && secQty > 0 ? prim * secQty : 0;
              return (
                <div>
                  <label className={labelClass}>Lote de Entrega (pç)</label>
                  <div className="flex gap-1">
                    <input type="number" min={1} placeholder="ex: 200"
                      value={(selectedCard as any).vsmDeliveryLot ?? ''}
                      onChange={(e) => onUpdateCardData(selectedCard.id, { vsmDeliveryLot: parseInt(e.target.value) || 0 } as any)}
                      className={inputClass + " text-right flex-1" + (isInvalid ? ' border-orange-400 focus:ring-orange-400 bg-orange-50 dark:bg-orange-900/10' : '')}
                      onFocus={(e) => e.target.select()}
                    />
                    {suggestedLot > 0 && (
                      <button
                        onClick={() => onUpdateCardData(selectedCard.id, { vsmDeliveryLot: suggestedLot } as any)}
                        title={`Sugerir lote baseado em 1 ${(selectedCard as any).vsmPackSecondaryName || 'Unid. Sec.'} = ${suggestedLot} pç`}
                        className="h-8 px-2 rounded-sm border border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-[9px] font-bold text-slate-500 hover:text-blue-600 transition-colors shrink-0 whitespace-nowrap"
                      >
                        Sugerir
                      </button>
                    )}
                  </div>
                  {/* Poka-Yoke visual */}
                  {lot > 0 && prim > 0 && (
                    isInvalid ? (
                      <div className="mt-1 flex items-start gap-1.5 px-2 py-1 rounded border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-600/50">
                        <span className="text-orange-500 shrink-0">⚠</span>
                        <span className="text-[10px] text-orange-700 dark:text-orange-400">
                          Lote {lot} não é múltiplo de {prim} ({(selectedCard as any).vsmPackPrimaryName || 'unid. prim.'})
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                        <span>✓</span>
                        <span>Lote válido — {lot / prim}× {(selectedCard as any).vsmPackPrimaryName || 'unid. prim.'}</span>
                      </div>
                    )
                  )}
                </div>
              );
            })()}

            {/* Resumo calculado */}
            {(() => {
              const prim    = Number((selectedCard as any).vsmPackPrimaryQty || 0);
              const secQty  = Number((selectedCard as any).vsmPackSecondaryQty || 0);
              if (prim <= 0 || secQty <= 0) return null;
              const pcsPerSec = prim * secQty;
              return (
                <div className="mt-2 flex items-center justify-between px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600/40">
                  <span className="text-[9px] text-amber-700 dark:text-amber-400">
                    1 {(selectedCard as any).vsmPackSecondaryName || 'Sec.'}
                  </span>
                  <span className="text-[9px] font-bold text-amber-800 dark:text-amber-300" style={{fontFamily:'monospace'}}>
                    = {secQty} {(selectedCard as any).vsmPackPrimaryName || 'Prim.'} / {pcsPerSec.toLocaleString('pt-BR')} pç
                  </span>
                </div>
              );
            })()}
            </div>
          </details>
        </div>
        )}
        </>
        )}

      </div>
    </div>
  );
};

export default RightPanel;
